import {Injectable, OnInit} from '@angular/core';
import {HttpClient, HttpErrorResponse, HttpHeaders, HttpParams, HttpResponse} from '@angular/common/http';
import {Observable, of, of as observableOf, throwError} from 'rxjs';
import {map, catchError, switchMap} from 'rxjs/operators';

import {environment} from '@env/environment';
import {APP_LAST_STEP, Application} from './models/application.model';
import {FTPConfig} from './models/ftp-config';
import {FTPUser} from './models/ftp-user.model';
import {CronConfig, CronConfigFactory, CronOptionType, ProcessConfig} from './models/process-config.model';
import {IntegrationTask, STREAM_TRANSFORMATIONS, TRANSFORMATIONS} from './models/integration-task.model';
import {AttributeMappingDirection, IntegrationTaskMap} from './models/integration-task-map.model';
import {
    DataAttribute,
    EduToneExtensionFields,
    EduToneExtensionSufix,
    EduToneExtensionTitleSufix,
    OneRosterExtensionFields,
    OneRosterExtensionSufix,
    OneRosterExtensionTitleSufix
} from './models/data-attribute.model';
import {OrganizationsMapping} from './models/organizationsMapping.model';
import {DomainOfUniqueness} from './models/domain-of-uniqueness.model';
import {
    DATA_SOURCE_TYPE,
    DataSourceFriendlyName,
    DataSourcePathMap,
    DataSourceType,
    isIncrementalSyncAllowed,
    SupportAmplifyStaffAccessMappingDataSourceTypes,
    SupportGradeMappingDataSourceTypes,
    SupportMappingRulesDataSourceTypes
} from './models/data-source-type.model';
import {
    DataSourceConfig,
    DefaultSchemaName,
    getDataSourceModifyParam,
    OneRoster10,
    OneRoster11,
    OneRoster12
} from './models/data-source-config.model';
import {
    Integration,
    INTEGRATION_TASK_TYPES,
    INTEGRATION_TASKS_BY_TYPES,
    INTEGRATION_TASKS_EDUTONE_TITLE_MAP,
    PAGES_WHERE_INTEGRATION_TASK_ARE_SHOWN,
} from './models/integration.model';
import {MatLegacySnackBar as MatSnackBar, MatLegacySnackBarConfig as MatSnackBarConfig} from '@angular/material/legacy-snack-bar';
import {IntegrationMode} from '@app/views/admin/application-settings/models/integration-mode.model';
import {DEFAULT_SFTP_LOCALHOST_PORT, RankOneDistrictItem, SFTPConfig} from '@app/views/admin/application-settings/models/sftp-config.model';
import {UntypedFormArray, UntypedFormGroup} from '@angular/forms';
import {DefaultSchemaList} from '@app/views/admin/application-settings/models/data-source-config.model';
import {MESSAGES} from '@app/core/model/messages';
import {
    ONEROSTER_V11_SUFFIX,
    ONEROSTER_V12_SUFFIX,
    OneRosterConfig,
    OneRosterDataSourceFriendlyNamesWithEnteredByUserAuthUrl
} from '@app/views/admin/application-settings/models/one-roster-config.model';
import {
    CourseraConfig,
    EDIT_DELIVERY_CONFIG_AND_ORGS_MAPPING_NOT_ALLOWED_COURSERA_CLIENT_ID
} from '@app/views/admin/application-settings/models/coursera-config.model';
import {IntegrationState} from '@app/views/admin/application-settings/models/integration-state.model';
import {NameConverter} from '@app/core/utils/converters';
import {OrgMapping} from '@app/views/admin/application-settings/models/orgMapping.model';
import {copyData, IntegrationRulesMap, RequestedFilterViewModel} from '@app/core/model/integration-models';
import {EntityFilterModel} from '@app/core/model/filtering/entity-filter-model';
import {PropertyFilterModel} from '@app/core/model/filtering/property-filter-model';
import {Argument} from '@app/core/model/filtering/argument';
import {DictionaryModel, GRADE_DICTIONARY_NAME} from '@app/views/admin/application-settings/models/dictionary.model';
import {DictionaryEntryModel} from '@app/views/admin/application-settings/models/dictionary-entry-model';
import {QwiklabsCourse, QwiklabsDictionaryEntryModel} from '@app/views/admin/application-settings/models/qwiklabs-course.model';
import {Store} from '@ngrx/store';
import * as fromApplicationSettings from '@app/views/admin/application-settings/store/application-settings.reducers';
import {
    LTI_CONFIG_VERSION,
    LTIConfig,
} from '@app/views/admin/application-settings/models/lti-config.model';
import {MasterFieldsMergingRule} from '@app/views/admin/application-settings/models/master-fields.model';
import {DomainOfUniquenessProperty} from '@app/views/admin/application-settings/models/domain-of-uniqueness-property.model';
import {
    ISchoolYearRolloverSettings,
    SchoolYearRolloverSettingsStatus,
    SchoolYearRolloverSettingsType
} from '@app/core/model/rollover-model';
import {SensitiveData} from '@app/views/admin/provider/sensitive-data/sensitive-data.model';
import {CustomerService} from '@app/core/service/customer.service';
import {CourseraCourse, CourseraProgram} from '@app/views/admin/application-settings/models/coursera-program.model';
import {VeracrossConfig} from '@app/views/admin/application-settings/models/veracross-config';
import {SnackBarService} from '@app/core/service/snack-bar.service';
import {FilterRuleMetadata} from '@app/core/model/filtering/filter-rule-metadata';
import {AeriesConfig} from '@app/views/admin/application-settings/models/aeries-config.model';
import {GoogleClassroomConfig} from '@app/views/admin/application-settings/models/google-classroom';
import {GSuiteConfig} from '@app/views/admin/application-settings/models/gsuite-config.model';
import {ManageBacConfig} from '@app/views/admin/application-settings/models/manage-bac-config.model';
import {QwiklabsConfig} from '@app/views/admin/application-settings/models/qwiklabs-config.model';
import {OpenApplyConfig} from '@app/views/admin/application-settings/models/open-apply-config.model';
import {SkywardConfig} from '@app/views/admin/application-settings/models/skyward-config.model';
import {IlluminatesConfig} from '@app/views/admin/application-settings/models/illuminate-config.model';
import {PowerSchoolConfig} from '@app/views/admin/application-settings/models/power-school-config.model';
import {HTTPRequestDataViewModel} from '@app/views/admin/application-settings/models/intercepted-http-call.model';
import {SharedPoolResponseData} from '@app/core/model/shared-pool.model';
import {
    FormulaOperations,
    LoginCredentialsFormula
} from '@app/views/admin/application-settings/components/login-credentials/entity-login-credentials/entity-login-credentials.component';
import {HOST_COURSERA, HOST_QWIKLABS} from '@app/views/administration/model/host.model';
import {GRADEBOOK_SYNC_FEATURE_TOGGLE_AUTO_TURNED_ON_SYNC_APPS} from '@app/views/common/feature-toggles/featue-toggle.model';
import {OrgItem} from '@app/views/admin/application-settings/models/orgItem.model';
import {LDAPConfig} from '@app/views/admin/application-settings/models/ldap-config.model';
import {AuditUserActivityService} from '@app/core/service/audit-user-activity.service';
import {Webhook} from '@app/views/admin/application-settings/models/provisioning.model';
import {IntegrationService} from '@app/core/service/integration.service';
import {DaysOfWeekLongFriendly, MonthDays} from '@app/core/utils/other-utils';
import {DecimalPipe} from '@angular/common';
import {ADAWIntegration} from '@app/views/admin/application-settings/models/adaw-application.model';
import moment from 'moment/moment';
import {EmailRelay} from '@app/views/admin/application-settings/components/basic/email-relay-info/email-relay.model';

@Injectable()
export class ApplicationSettingsService implements OnInit {

    readonly SAVE_INTEGRATION_ENTITY_MAPPING_ERROR_MESSAGE = 'Unknown error occurred while saving Entity Mapping data.';
    private readonly SAVE_EDIT_INTEGRATION_ERROR_MESSAGE = 'Sorry, you can`t delete Data Schema that is in usage.';

    static getExtendedIntegrationTask(
        task: IntegrationTask, integration: Integration, sortAttributeList: boolean = true
    ): IntegrationTask {

        const eduToneDS: string = (integration.source.dataSourceType === DataSourceType.EduTone) ? 'source' : 'target';
        const otherDS: string = (integration.source.dataSourceType === DataSourceType.EduTone) ? 'target' : 'source';

        if ((integration.mode === IntegrationMode.IMPORT) || (integration.mode === IntegrationMode.EXPORT)) {
            task.mappings = task.mappings.filter((mapping: IntegrationTaskMap) => mapping[`${otherDS}PropertyName`]);
        }

        // exclude SPED attributes in all cases except SysAdmin creates DataSchema
        const isDefaultIntegration = integration.state === IntegrationState.DEFAULT;
        if (!isDefaultIntegration
            && ((integration.mode === IntegrationMode.IMPORT) || (integration.mode === IntegrationMode.EXPORT))
            && (integration[otherDS].dataSourceType === DataSourceType.SFTP)
            && ((integration[otherDS].defaultSchemaName === DefaultSchemaName.ONE_ROSTER_10)
                || (integration[otherDS].defaultSchemaName === DefaultSchemaName.ONE_ROSTER_11)
                || (integration[otherDS].defaultSchemaName === DefaultSchemaName.ONE_ROSTER_12))) {

            task.restrictedMappings = [];
            task.mappings = task.mappings.filter((mapping: IntegrationTaskMap) => {
                if (RESTRICTED_ONE_ROSTER_1_1_CSV_IMPORT_ATTRIBUTES[mapping[`${eduToneDS}PropertyName`]]) {
                    task.restrictedMappings.push(mapping);
                }
                return !RESTRICTED_ONE_ROSTER_1_1_CSV_IMPORT_ATTRIBUTES[mapping[`${eduToneDS}PropertyName`]];
            });

            task[`${eduToneDS}AttributeList`] = task[`${eduToneDS}AttributeList`].filter((attr: DataAttribute) =>
                !RESTRICTED_ONE_ROSTER_1_1_CSV_IMPORT_ATTRIBUTES[attr.propertyName]
            );
        }

        const sourceAttributeListObj = {}; // { attribute.propertyName : DataAttribute }
        const targetAttributeListObj = {}; // { attribute.propertyName : DataAttribute }
        task.sourceAttributeList.forEach((attribute: DataAttribute) => sourceAttributeListObj[attribute.propertyName] = attribute);
        task.targetAttributeList.forEach((attribute: DataAttribute) => targetAttributeListObj[attribute.propertyName] = attribute);

        const sourceExtensionFields: string[] = [];
        if ((integration.mode === IntegrationMode.IMPORT)
            && (([OneRoster10, OneRoster11, OneRoster12].indexOf(integration.source.defaultSchemaName) > -1)
                || (<DataSourceType>integration.source.dataSourceType === DataSourceType.OneRoster))) {
            OneRosterExtensionFields.forEach((propertyName: string) => {
                const extensionParentField = sourceAttributeListObj[propertyName];
                if (extensionParentField) {
                    task.sourceAttributeList.push(
                        new DataAttribute(
                            extensionParentField.propertyName + OneRosterExtensionSufix,
                            extensionParentField.friendlyName + OneRosterExtensionTitleSufix,
                            extensionParentField.domain,
                            extensionParentField.entityTypeDenom,
                            'String',
                            extensionParentField.ownerRef,
                            extensionParentField.order,
                            false,
                            false,
                            false,
                            true
                        ));
                    sourceExtensionFields.push(extensionParentField.propertyName);
                }
            });
        }

        const targetExtensionFields: string[] = [];
        if ((integration.mode === IntegrationMode.IMPORT) || (integration.mode === IntegrationMode.SYNC)) {
            EduToneExtensionFields.forEach((propertyName: string) => {
                const extensionParentField = targetAttributeListObj[propertyName];
                if (extensionParentField) {
                    task.targetAttributeList.push(
                        new DataAttribute(
                            extensionParentField.propertyName + EduToneExtensionSufix,
                            extensionParentField.friendlyName + EduToneExtensionTitleSufix,
                            extensionParentField.domain,
                            extensionParentField.entityTypeDenom,
                            'String',
                            extensionParentField.ownerRef,
                            extensionParentField.order,
                            false,
                            false,
                            false,
                            true
                        ));
                    targetExtensionFields.push(extensionParentField.propertyName);
                }
            });
        }

        task.mappings = task.mappings
            .map((mapping: IntegrationTaskMap) => {
                let sourceExtensionPropertyName = null;
                const sourcePropertyName = (mapping.sourcePropertyName || mapping.backToSourcePropertyName);
                let sourceProperty = sourceAttributeListObj[sourcePropertyName];
                if (!sourceProperty) {
                    sourceExtensionPropertyName = sourceExtensionFields
                        .find((propertyName: string) =>
                            (sourcePropertyName
                            && (sourcePropertyName !== propertyName)
                            && (sourcePropertyName.indexOf(propertyName) === 0))
                        );

                    if (sourceExtensionPropertyName) {
                        sourceProperty = sourceAttributeListObj[sourceExtensionPropertyName + OneRosterExtensionSufix];
                    }
                }

                let targetExtensionPropertyName = null;
                let targetProperty = targetAttributeListObj[mapping.targetPropertyName];
                if (!targetProperty) {
                    targetExtensionPropertyName = targetExtensionFields
                        .find((propertyName: string) =>
                            (mapping.targetPropertyName
                            && (mapping.targetPropertyName !== propertyName)
                            && (mapping.targetPropertyName.indexOf(propertyName) === 0))
                        );

                    if (targetExtensionPropertyName) {
                        targetProperty = targetAttributeListObj[targetExtensionPropertyName + EduToneExtensionSufix];
                    }
                }

                mapping.sourceProperty = sourceProperty
                    || new DataAttribute(sourcePropertyName);
                mapping.targetProperty = targetProperty
                    || new DataAttribute(mapping.targetPropertyName);

                if (sourceExtensionPropertyName) {
                    const sourceExtensionName = sourcePropertyName
                        .substr(sourceExtensionPropertyName.length + 1, sourcePropertyName.length);
                    const sourceExtensionProperty = sourceAttributeListObj[sourceExtensionPropertyName];

                    mapping.sourceProperty = new DataAttribute(
                        sourcePropertyName,
                        (mapping.sourceProperty.friendlyName || sourceExtensionProperty.friendlyName)
                            + ' ' + sourceExtensionName.charAt(0).toUpperCase() + sourceExtensionName.slice(1),
                        mapping.sourceProperty.domain,
                        mapping.sourceProperty.entityTypeDenom,
                        mapping.sourceProperty.propertyTypeDenom,
                        mapping.sourceProperty.ownerRef,
                        mapping.sourceProperty.order,
                        false,
                        false,
                        false,
                        false,
                        'Attribute name: ' + sourcePropertyName,
                        false,
                        true
                    );
                    task.sourceAttributeList.push(mapping.sourceProperty);
                }

                if (targetExtensionPropertyName) {
                    const targetExtensionName = mapping.targetPropertyName
                        .substr(targetExtensionPropertyName.length + 1, mapping.targetPropertyName.length);
                    const targetExtensionProperty = targetAttributeListObj[targetExtensionPropertyName];

                    mapping.targetProperty = new DataAttribute(
                        mapping.targetPropertyName,
                        (mapping.targetProperty.friendlyName || targetExtensionProperty.friendlyName)
                            + ' ' + targetExtensionName.charAt(0).toUpperCase() + targetExtensionName.slice(1),
                        mapping.targetProperty.domain,
                        mapping.targetProperty.entityTypeDenom,
                        mapping.targetProperty.propertyTypeDenom,
                        mapping.targetProperty.ownerRef,
                        mapping.targetProperty.order,
                        false,
                        false,
                        false,
                        false,
                        'Attribute name: ' + mapping.targetPropertyName,
                        false,
                        true
                    );
                    task.targetAttributeList.push(mapping.targetProperty);
                }
                return mapping;
            });

        task.mappings = ApplicationSettingsService.setMappingsDirection(integration, task);

        // split attributes
        const isOpenApplyOrManageBac = (integration[otherDS].dataSourceType === DataSourceType.OpenApply)
            || (integration[otherDS].dataSourceType === DataSourceType.ManageBac);
        if (integration.mode === IntegrationMode.SYNC && (isOpenApplyOrManageBac || isDefaultIntegration)) {
            task.mappings = ApplicationSettingsService.splitTaskMapping(task, otherDS);
        }

        task.mappings.sort((mapping1: IntegrationTaskMap, mapping2: IntegrationTaskMap) =>
            (mapping1[`${eduToneDS}Property`].friendlyName || mapping1[`${eduToneDS}Property`].propertyName || '').toLowerCase().localeCompare(
                (mapping2[`${eduToneDS}Property`].friendlyName || mapping2[`${eduToneDS}Property`].propertyName || '').toLowerCase()
            ));
        task.mappings.sort((mapping1: IntegrationTaskMap, mapping2: IntegrationTaskMap) =>
            mapping1[`${eduToneDS}Property`].order - mapping2[`${eduToneDS}Property`].order);

        task.mappings = task.mappings.map((mapping: IntegrationTaskMap, mappingIndex: number) => {
            mapping.mappingIndex = mappingIndex;
            return mapping;
        });

        // exclude target attributes for Amplify skin
        const IS_AMPLIFY_CUSTOMER = CustomerService.checkIsAmplifyCustomer();
        if (IS_AMPLIFY_CUSTOMER && !isDefaultIntegration) {
            task.targetAttributeList = task.targetAttributeList.filter((attribute: DataAttribute) =>
                !RESTRICTED_AMPLIFY_SKIN_TARGET_ATTRIBUTES[attribute.propertyName]);
        }

        if (integration[otherDS].defaultSchemaName) {
            const isCustomSchema = DefaultSchemaList.indexOf(<DefaultSchemaName>integration[otherDS].defaultSchemaName) === -1;
            if (isCustomSchema) {
                task.mappings.forEach((mapping: IntegrationTaskMap) => {
                    const addAttribute = !(task[otherDS + 'AttributeList']
                        .find((attr: DataAttribute) => attr.propertyName === mapping[otherDS + 'Property'].propertyName));
                    if (addAttribute) {
                        task[otherDS + 'AttributeList'].push(mapping[otherDS + 'Property']);
                    }
                    const propertyName =
                        (!mapping[otherDS + 'Property'].propertyName && (otherDS === 'source' && mapping.backToSourcePropertyName))
                            ? mapping.backToSourcePropertyName
                            : mapping[otherDS + 'Property'].propertyName;
                    mapping[otherDS + 'Property'].friendlyName = NameConverter.propertyToFriendlyName(propertyName);
                });
            }
        }

        if (sortAttributeList) {
            task.sourceAttributeList.sort((attr1: DataAttribute, attr2: DataAttribute) =>
                (attr1.friendlyName || attr1.propertyName).toLowerCase().localeCompare(
                    (attr2.friendlyName || attr2.propertyName).toLowerCase()
                ));

            task.targetAttributeList.sort((attr1: DataAttribute, attr2: DataAttribute) =>
                (attr1.friendlyName || attr1.propertyName).toLowerCase().localeCompare(
                    (attr2.friendlyName || attr2.propertyName).toLowerCase()
                ));
        }

        task = this.addTaskProperties(task);

        return task;
    }

    static setMappingsDirection(integration: Integration, task: IntegrationTask): IntegrationTaskMap[] {
        return task.mappings
            .map((mapping: IntegrationTaskMap) => {
                if (integration.mode === IntegrationMode.SYNC) {
                    if (mapping.backToSourcePropertyName && !mapping.sourcePropertyName) {
                        mapping.direction = AttributeMappingDirection.EXPORT;
                    }
                    if (mapping.sourcePropertyName && !mapping.backToSourcePropertyName) {
                        mapping.direction = AttributeMappingDirection.IMPORT;
                    }
                    if (mapping.backToSourcePropertyName === mapping.sourcePropertyName) {
                        mapping.direction = AttributeMappingDirection.SYNC;
                    }
                }

                if (integration.mode === IntegrationMode.EXPORT) {
                    if ((mapping.sourcePropertyName && !mapping.backToSourcePropertyName)
                        || (mapping.sourcePropertyName === mapping.backToSourcePropertyName)) {
                        mapping.direction = AttributeMappingDirection.EXPORT;
                    } else {
                        if (!mapping.sourcePropertyName && mapping.backToSourcePropertyName) {
                            mapping.direction = AttributeMappingDirection.IMPORT;
                        } else {
                            mapping.direction = AttributeMappingDirection.EXPORT;
                        }
                    }
                }

                if (integration.mode === IntegrationMode.IMPORT) {
                    if ((mapping.sourcePropertyName && !mapping.backToSourcePropertyName)
                        || (mapping.sourcePropertyName === mapping.backToSourcePropertyName)) {
                        mapping.direction = AttributeMappingDirection.IMPORT;
                    } else {
                        if (!mapping.sourcePropertyName && mapping.backToSourcePropertyName) {
                            mapping.direction = AttributeMappingDirection.EXPORT;
                        } else {
                            mapping.direction = AttributeMappingDirection.IMPORT;
                        }
                    }
                }

                return mapping;
            });
    }

    static getExtendedProcessConfig(
        processConfigModel: ProcessConfig[],
        otherDataSourceType?: DataSourceType,
        mode?: IntegrationMode,
        defaultSchemaName?: DefaultSchemaName,
        isSupportIncrementalSyncFeatureToggleIsActive: boolean = false
    ): ProcessConfig[] {
        processConfigModel = processConfigModel ? processConfigModel : [];

        if (!processConfigModel.some((processConfig: ProcessConfig) => !processConfig.incremental)) {
            processConfigModel.push(new ProcessConfig(false));
        }
        if (!processConfigModel.some((processConfig: ProcessConfig) => processConfig.incremental) && otherDataSourceType) {
            if (isIncrementalSyncAllowed(otherDataSourceType, mode, defaultSchemaName, isSupportIncrementalSyncFeatureToggleIsActive)) {
                processConfigModel.push(new ProcessConfig(true));
            }
        }

        processConfigModel.sort((processConfig1: ProcessConfig, processConfig2: ProcessConfig) => {
            if (processConfig1.incremental === processConfig2.incremental) {
                return (processConfig1.processId).toString().localeCompare((processConfig2.processId).toString());
            } else {
                return Number(processConfig1.incremental) - Number(processConfig2.incremental);
            }
        });
        processConfigModel.forEach((processConfig: ProcessConfig) =>
            processConfig.cronConfig = CronConfigFactory.createFromCronExpression(processConfig.cronExpression, (processConfig.processId !== null))
        );

        return processConfigModel;
    }

    static getNextSyncDateList(processConfigModel: any[], app: ADAWIntegration): Date[] {
        const nextSyncDateList: moment.Moment[] = [];
        processConfigModel.forEach((processConfig: ProcessConfig) => {
            processConfig.cronConfig = CronConfigFactory.createFromCronExpression(processConfig.cronExpression, (processConfig.processId !== null));

            const minute = processConfig?.cronConfig?.minute;
            if (processConfig?.cronConfig?.cronOption === CronOptionType.DAILY) {
                processConfig?.cronConfig?.hours.forEach((hour: number) => {
                    const syncDate = moment(`${hour}:${minute}`, 'hh:mm').second(0);
                    const nextSyncDate = syncDate.isAfter(moment.now()) ? syncDate : syncDate.add(1, 'days');
                    nextSyncDateList.push(nextSyncDate);
                });
            }

            if (processConfig?.cronConfig?.cronOption === CronOptionType.WEEKLY) {
                processConfig?.cronConfig?.daysOfWeek.forEach((weekDayNumber: number) => {
                    processConfig?.cronConfig?.hours.forEach((hour: number) => {
                        const syncDate = moment().day(weekDayNumber).hour(hour).minute(minute).second(0);
                        const nextSyncDate = syncDate.isAfter(moment.now()) ? syncDate : syncDate.add(1, 'week');
                        nextSyncDateList.push(nextSyncDate);
                    });
                });
            }

            if (processConfig?.cronConfig?.cronOption === CronOptionType.MONTHLY) {
                processConfig?.cronConfig?.daysOfMonth.forEach((dayOfMonth: number) => {
                    processConfig?.cronConfig?.hours.forEach((hour: number) => {
                        const syncDate = moment().date(dayOfMonth).hour(hour).minute(minute).second(0);
                        const nextSyncDate = syncDate.isAfter(moment.now()) ? syncDate : syncDate.add(1, 'month');
                        nextSyncDateList.push(nextSyncDate);
                    });
                });
            }
        });

        nextSyncDateList.sort((dateA: moment.Moment, dateB: moment.Moment) =>
            moment.utc(dateA).diff(moment.utc(dateB))
        );
        return nextSyncDateList.map((value: moment.Moment) => value.toDate());
    }

    static getEntityName(entityName: string): string {
        return (INTEGRATION_TASKS_EDUTONE_TITLE_MAP[entityName] ? INTEGRATION_TASKS_EDUTONE_TITLE_MAP[entityName] : entityName);
    }

    static splitTaskMapping(task: IntegrationTask, otherDS: string): IntegrationTaskMap[] {
        const splitedMappings = [];
        const newMappings = task.mappings.map((mapping: IntegrationTaskMap) => {
            if (mapping.backToSourcePropertyName
                && mapping[`${otherDS}PropertyName`]
                && (mapping[`${otherDS}PropertyName`] !== mapping.backToSourcePropertyName)) {

                const splitedMapping = copyData(mapping);
                splitedMapping[`${otherDS}PropertyName`] = null;

                splitedMapping[`${otherDS}Property`] = task[`${otherDS}AttributeList`]
                    .find((attribute: DataAttribute) => attribute.propertyName === mapping.backToSourcePropertyName)
                    || new DataAttribute(mapping.backToSourcePropertyName);

                splitedMappings.push(splitedMapping);

                mapping.backToSourcePropertyName = null;
            }
            return mapping;
        });

        return newMappings.concat(splitedMappings);
    }

    static combineIntegrationTaskMapAttributes(integrationTasks: IntegrationTask[]): IntegrationTask[] {
        const tasks = integrationTasks.map((task: IntegrationTask) => {

            const targetPropertyNamesObj = ApplicationSettingsService.getTargetPropertyNamesObj(task);
            const newMappings = ApplicationSettingsService.combineMappings(task, targetPropertyNamesObj);

            task.mappings = newMappings;
            return task;
        });

        return tasks;
    }

    static getTargetPropertyNamesObj(task: IntegrationTask): {} {
        const targetPropertyNamesObj = {};

        task.mappings.forEach((mapping: IntegrationTaskMap) => {
            if (!<IntegrationTaskMap>targetPropertyNamesObj[mapping.targetPropertyName]) {
                (<IntegrationTaskMap[]>targetPropertyNamesObj[mapping.targetPropertyName]) = [mapping];
            } else {
                (<IntegrationTaskMap[]>targetPropertyNamesObj[mapping.targetPropertyName]).push(mapping);
            }
        });
        return targetPropertyNamesObj;
    }

    static combineMappings(task: IntegrationTask, targetPropertyNamesObj: {}): IntegrationTaskMap[] {
        let newMappings: IntegrationTaskMap[] = <IntegrationTaskMap[]>[];

        task.mappings.forEach((mapping: IntegrationTaskMap) => {

            if (targetPropertyNamesObj[mapping.targetPropertyName]) {
                const mappingsListWithCurrentTargetPropertyName = <IntegrationTaskMap[]>targetPropertyNamesObj[mapping.targetPropertyName];

                if (mappingsListWithCurrentTargetPropertyName.length === 1) {
                    newMappings.push(mapping);
                } else {

                    mappingsListWithCurrentTargetPropertyName
                        .sort((mapping1: IntegrationTaskMap, mapping2: IntegrationTaskMap) => mapping1.sourcePropertyName ? -1 : 1);

                    if (mappingsListWithCurrentTargetPropertyName.length === 2) {
                        newMappings.push(ApplicationSettingsService.combineTwoMappings(mappingsListWithCurrentTargetPropertyName));

                    } else {
                        const listOfFirstTwoMappings = <IntegrationTaskMap[]>[];
                        const listOfRestMappings = <IntegrationTaskMap[]>[];

                        // with purpose to combine mappings with the same mappingId
                        const secondMappingIndex = mappingsListWithCurrentTargetPropertyName
                            .findIndex((taskMap: IntegrationTaskMap, mappingIndex: number) =>
                                (mappingIndex !== 0) && (taskMap.mappingId === mappingsListWithCurrentTargetPropertyName[0].mappingId)
                            );
                        if (secondMappingIndex !== -1) {
                            // with purpose to combine mappings with the same mappingId
                            // add to listOfFirstTwoMappings two mappings with the same mappingId
                            listOfFirstTwoMappings.push(mappingsListWithCurrentTargetPropertyName[0]);
                            listOfFirstTwoMappings.push(mappingsListWithCurrentTargetPropertyName[secondMappingIndex]);
                            mappingsListWithCurrentTargetPropertyName.splice(secondMappingIndex, 1);
                            mappingsListWithCurrentTargetPropertyName.splice(0, 1);
                        }

                        mappingsListWithCurrentTargetPropertyName.forEach((taskMap: IntegrationTaskMap, mappingIndex: number) => {
                            if (mappingIndex < 2) {
                                listOfFirstTwoMappings.push(taskMap);
                            } else {
                                listOfRestMappings.push(taskMap);
                            }
                        });

                        newMappings.push(ApplicationSettingsService.combineTwoMappings(listOfFirstTwoMappings));
                        newMappings = newMappings.concat(listOfRestMappings);
                    }
                    delete targetPropertyNamesObj[mapping.targetPropertyName];
                }
            }
        });
        return newMappings;
    }

    static combineTwoMappings(mappings: IntegrationTaskMap[]): IntegrationTaskMap {
        const combinedMapping = mappings[0];
        combinedMapping.backToSourcePropertyName = mappings[1].backToSourcePropertyName;
        return combinedMapping;
    }

    private static addTaskProperties(task: IntegrationTask): IntegrationTask {
        if (!task.transformations) {
            task.transformations = <EntityFilterModel[]>[];
        }

        if (!task.validations) {
            task.validations = <EntityFilterModel[]>[];
        }

        if (!task.streamTransformations) {
            task.streamTransformations = <EntityFilterModel[]>[];
        }

        if (!task.domainsOfUniqueness) {
            task.domainsOfUniqueness = <DomainOfUniqueness[]>[];
        }

        if (!task.restrictedMappings) {
            task.restrictedMappings = <IntegrationTaskMap[]>[];
        }
        return task;
    }

    static getNotFakePasswordValue(formValue: string | null, dname: string, dataSourceConfigProperty: string): string | null {
        let result = formValue ? formValue.trim() : formValue;
        if (result === FAKE_PASSWORD_VALUE) {
            result = '';
        }
        if (!result
            && dataSourceConfigProperty
            && (dataSourceConfigProperty !== FAKE_PASSWORD_VALUE)) {
            result = dataSourceConfigProperty;
        }

        return result;
    }

    static setFakePasswordValue(dataSourceConfigProperty: any): string | null {
        return !dataSourceConfigProperty ? FAKE_PASSWORD_VALUE : dataSourceConfigProperty;
    }

    static getIntegrationState(
        isVendor: boolean, isProvider: boolean, isSysAdmin: boolean, isDataSchema: boolean = false
    ): IntegrationState {
        let integrationState: IntegrationState;
        if (isSysAdmin || isDataSchema) {
            integrationState = IntegrationState.DEFAULT;
        }
        if (isVendor) {
            integrationState = IntegrationState.DRAFT;
        }
        if (isProvider || (isSysAdmin && !isDataSchema)) {
            integrationState =  IntegrationState.ORIGINAL;
        }
        return integrationState;
    }

    static getIntegrationMode(
        isVendor: boolean, isProvider: boolean = false, isSysAdmin: boolean = false, isDataSchema: boolean = false
    ): IntegrationMode {
        let integrationMode: IntegrationMode;
        if (isSysAdmin || isDataSchema) {
            integrationMode =  IntegrationMode.SYNC;
        }
        if (isVendor) {
            integrationMode = IntegrationMode.EXPORT;
        }
        if (isProvider || (isSysAdmin && !isDataSchema)) {
            integrationMode =  IntegrationMode.IMPORT;
        }
        return integrationMode;
    }

    static trimStringValuesOfObject(newDataSourceConfig) {
        for (const key in newDataSourceConfig) {
            if (newDataSourceConfig.hasOwnProperty(key) && newDataSourceConfig[key] && (typeof newDataSourceConfig[key] === 'string')) {
                newDataSourceConfig[key] = newDataSourceConfig[key].trim();
            }
        }
    }

    static isSupportMappingRulesDataSourceTypes(
        application: Application, dataSourceConfig: DataSourceConfig, integration: Integration
    ): boolean {

        const otherDS: string = integration && integration.source
            ? (integration.source.dataSourceType === DataSourceType.EduTone) ? 'target' : 'source'
            : null;

        return (dataSourceConfig && SupportMappingRulesDataSourceTypes.includes(dataSourceConfig.dataSourceType))
            || (application && SupportMappingRulesDataSourceTypes.includes(application.dataSourceType))
            || (integration && integration[otherDS] && SupportMappingRulesDataSourceTypes.includes(integration[otherDS].dataSourceType));
    }

    static isSupportGradeMappingDataSourceTypes(integration: Integration): boolean {
        if (integration
            && integration.integrationId
            && (integration && integration.source && SupportGradeMappingDataSourceTypes.includes(integration.source.dataSourceType))
            && integration.dictionaries) {
            return integration.dictionaries.some((dictionary: DictionaryModel) =>
                dictionary.dictionaryName && (dictionary.dictionaryName.toLocaleLowerCase() === GRADE_DICTIONARY_NAME.toLocaleLowerCase()));
        }
        return false;
    }

    static isSupportAmplifyStaffAccessMappingDataSourceTypes(integration: Integration): boolean {
        return integration
            && integration.integrationId
            && integration.mode === IntegrationMode.EXPORT
            && integration.target
            && SupportAmplifyStaffAccessMappingDataSourceTypes.includes(integration.target.dataSourceType)
            && integration.target.defaultSchemaName === DefaultSchemaName.AMPLIFY;
    }

    static isSupportSetLoginCredentials(application: Application): boolean {
        return application?.dataSourceType === DataSourceType.GSuite
            && application?.modes?.includes(<string>IntegrationMode.SYNC);
    }

    static isSupportEntitySelection(application: Application, defaultApplication: Application): boolean {
        return application?.dataSourceType === DataSourceType.GSuite
            && application?.modes?.includes(<string>IntegrationMode.SYNC)
            && defaultApplication?.name === DataSourceFriendlyName['Google Workspace IDM'];
    }

    static sortPropertyFiltersByOUPath(propertyFilters: PropertyFilterModel[], orgUnitPath: string): void {
        propertyFilters.sort((a: PropertyFilterModel, b: PropertyFilterModel) => {

            if (a.propertyName === orgUnitPath) {
                return -1;
            }
            if (b.propertyName === orgUnitPath) {
                return 1;
            }

            return a.arguments[0].id.localeCompare(b.arguments[0].id);
        });
    }

    static sortMergingRulesByPropertiesFriendlyName(domainOfUniqueness: DomainOfUniqueness[] = []): DomainOfUniqueness[]  {
        domainOfUniqueness.map((domainOfUniqueness: DomainOfUniqueness) => {
            domainOfUniqueness.properties = domainOfUniqueness.properties.sort((a: DomainOfUniquenessProperty, b: DomainOfUniquenessProperty) => (a.friendlyName || '').localeCompare(b.friendlyName || ''));
            return domainOfUniqueness;
        }).sort((a: DomainOfUniqueness, b: DomainOfUniqueness) => {
            const friendlyNamesPropertiesA = a.properties.map(prop => prop.friendlyName || '').join('');
            const friendlyNamesPropertiesB = b.properties.map(prop => prop.friendlyName || '').join('');

            return friendlyNamesPropertiesA.localeCompare(friendlyNamesPropertiesB);
        });

        return domainOfUniqueness;
    }

    static copyIntegration(sourceIntegration: Integration): Integration {
        const targetIntegration = copyData(sourceIntegration);

        // copy CustomValues Map for transformations and streamTransformations
        if (sourceIntegration && sourceIntegration.tasks) {
            // IntegrationTask.transformations property name
            ApplicationSettingsService.copyCustomValuesMapForTasksObjects(
                sourceIntegration.tasks, targetIntegration.tasks, TRANSFORMATIONS);
            // IntegrationTask.streamTransformations property name
            ApplicationSettingsService.copyCustomValuesMapForTasksObjects(
                sourceIntegration.tasks, targetIntegration.tasks, STREAM_TRANSFORMATIONS);
        }

        return targetIntegration;
    }

    static copyCustomValuesMapForTasksObjects(
        sourceIntegrationTasks: IntegrationTask[], targetIntegrationTasks: IntegrationTask[], objectName: string, copyTasks = false) {

        if (sourceIntegrationTasks && sourceIntegrationTasks.length > 0) {
            sourceIntegrationTasks.forEach((task: IntegrationTask, taskIndex: number) => {

                if (task[objectName] && task[objectName].length > 0) {
                    task[objectName].forEach((obj: EntityFilterModel, index: number) => {

                        targetIntegrationTasks[taskIndex][objectName][index].customValues =
                            ApplicationSettingsService.copyCustomValuesMaps(obj.customValues);
                    });
                }
            });
        }
    }

    static copyCustomValuesMaps(customValues: Map<string, Argument>): Map<string, Argument> {
        const newCustomValues = new Map<string, Argument>();
        if (customValues && (customValues.size > 0)) {
            customValues.forEach((value: Argument, key: string) => newCustomValues.set(key, value));
        }
        return newCustomValues;
    }


    static copyMap(mapObj: Map<string, any>): Map<string, any> {
        let result: Map<string, any>;
        if (mapObj instanceof Map) {
            result = new Map<string, any>(Object.entries(Object.fromEntries(mapObj)));
        } else {
            result = copyData(mapObj);
        }
        return result;
    }

    static copyIntegrationTasks(sourceIntegrationTasks: IntegrationTask[]): IntegrationTask[] {
        const targetIntegrationTasks = copyData(sourceIntegrationTasks);
        // copy CustomValues Map for transformations and streamTransformations
        // IntegrationTask.transformations property name
        ApplicationSettingsService.copyCustomValuesMapForTasksObjects(sourceIntegrationTasks, targetIntegrationTasks, TRANSFORMATIONS);
        // IntegrationTask.streamTransformations property name
        ApplicationSettingsService.copyCustomValuesMapForTasksObjects(sourceIntegrationTasks, targetIntegrationTasks, STREAM_TRANSFORMATIONS);
        return targetIntegrationTasks;
    }

    static getUniqueTasksObj(integrationTasks: IntegrationTask[], eduToneDS: string, otherDS: string): {} {
        const uniqueTasksObj = {}; // { ${eduToneDS}Type : IntegrationTask[] }
        integrationTasks.forEach((task: IntegrationTask) => {
            if (!uniqueTasksObj[task[`${eduToneDS}Type`]]) {
                uniqueTasksObj[task[`${eduToneDS}Type`]] = [task];
            } else {
                uniqueTasksObj[task[`${eduToneDS}Type`]].push(task);
                uniqueTasksObj[task[`${eduToneDS}Type`]]
                    .sort((task1: IntegrationTask, task2: IntegrationTask) =>
                        (task1[`${otherDS}Type`]).toLowerCase().localeCompare(
                            (task2[`${otherDS}Type`]).toLowerCase()));
            }
        });
        return uniqueTasksObj;
    }

    static getItemFromLocalStorage(keyName: string): string {
        return JSON.parse(window.localStorage.getItem(keyName));
    }

    static setItemToLocalStorage(keyName: string, value: string): void {
        window.localStorage.setItem(keyName, JSON.stringify(value));
    }

    static setCompositeEntryKeyForSaveQwiklabsDictionaries(qwiklabsCourse: QwiklabsCourse, deploymentId: string, sessionIndex?: number): string {
        return `${qwiklabsCourse.courseLibrary}#${qwiklabsCourse.courseSlug}#${deploymentId}${(sessionIndex || (sessionIndex === 0)) ? '#' + sessionIndex : '' }`;
    }

    static setCompositeEntryEdutoneNameForSaveQwiklabsDictionaries(entry: QwiklabsDictionaryEntryModel): string {
        return `${entry.edutoneName}#${entry.startDate}#${entry.endDate}`;
    }

    static parseEntryExternalNameOfQwiklabsCourseMapping(externalName: string) {
        let result = externalName;
        if (externalName) {
            const lastComma = externalName.lastIndexOf(', ');
            if (lastComma > -1) {
                result = externalName.substring(lastComma + 1).trim();
            }
        }
        return result;
    }

    static transformOldQwiklabsClassesSectionName(courseOrSectionName: string): {newEdutoneName: string; isGG4LCourseSelected: boolean} {

        let newEdutoneName = courseOrSectionName;
        let isCourse: boolean;
        if (courseOrSectionName) {
            const COURSE_ADDITIONAL_PARAM = 'course:';
            const SECTION_ADDITIONAL_PARAM = 'section:';

            const sectionSignIndex = courseOrSectionName.indexOf(SECTION_ADDITIONAL_PARAM);
            const courseSignIndex = courseOrSectionName.indexOf(COURSE_ADDITIONAL_PARAM);
            isCourse = courseSignIndex > -1;

            const startCopyIndex = sectionSignIndex > -1
                ? SECTION_ADDITIONAL_PARAM.length
                : isCourse ? COURSE_ADDITIONAL_PARAM.length : 0;

            newEdutoneName = courseOrSectionName.substring(startCopyIndex);
        }
        return {newEdutoneName: newEdutoneName, isGG4LCourseSelected: isCourse};
    }

    static getDateWithoutHours(date: Date): Date {
        return new Date(date.setHours(0, 0, 0, 0));
    }

    static isDisableDataDeletionDueRolloverPeriod(schoolYearRolloverSettings: ISchoolYearRolloverSettings): boolean {
        return schoolYearRolloverSettings
            && schoolYearRolloverSettings[SchoolYearRolloverSettingsType.DisableDataDeletion]
            && schoolYearRolloverSettings[SchoolYearRolloverSettingsType.DisableDataDeletion].isEnabled
            && schoolYearRolloverSettings[SchoolYearRolloverSettingsType.DisableDataDeletion].status == SchoolYearRolloverSettingsStatus.BEGAN;
    }

    static isSuspendDataImportDueRolloverPeriod(schoolYearRolloverSettings: ISchoolYearRolloverSettings): boolean {
        return schoolYearRolloverSettings
            && schoolYearRolloverSettings[SchoolYearRolloverSettingsType.SuspendDataImport]
            && schoolYearRolloverSettings[SchoolYearRolloverSettingsType.SuspendDataImport].isEnabled
            && schoolYearRolloverSettings[SchoolYearRolloverSettingsType.SuspendDataImport].status == SchoolYearRolloverSettingsStatus.BEGAN;
    }

    static isTaskVisible(
        pageTitle: string, taskType: string, dataSourceType?: DataSourceType, dataSourceConfig?: DataSourceConfig
    ): boolean {
        const pageAvailableTasksList = ApplicationSettingsService.getPageAvailableTasksList(pageTitle, dataSourceType, dataSourceConfig);
        return pageAvailableTasksList.includes(taskType);
    }

    static getVisibleTasksCount(integration: Integration, pageTitle: string, dataSourceConfig?: DataSourceConfig): number {
        const eduToneDS = (integration.source.dataSourceType === DataSourceType.EduTone) ? 'source' : 'target';
        const otherDS = (integration.source.dataSourceType === DataSourceType.EduTone) ? 'target' : 'source';
        return ApplicationSettingsService.getPageVisibleTasksList(
            integration.tasks, pageTitle, integration[otherDS].dataSourceType, eduToneDS, dataSourceConfig)
            .length;
    }

    static getPageVisibleTasksList(integrationTasks: IntegrationTask[],
        pageTitle: string,
        dataSourceType: DataSourceType,
        eduToneDS: string,
        dataSourceConfig?: DataSourceConfig
    ): IntegrationTask[] {
        const pageAvailableTasksList = ApplicationSettingsService.getPageAvailableTasksList(pageTitle, dataSourceType, dataSourceConfig);
        return integrationTasks.filter((task: IntegrationTask) => pageAvailableTasksList.includes(task[`${eduToneDS}Type`]));
    }

    static getPageAvailableTasksList(pageTitle: string, dataSourceType?: DataSourceType, dataSourceConfig?: DataSourceConfig): string[] {
        switch (pageTitle) {
            case PAGES_WHERE_INTEGRATION_TASK_ARE_SHOWN.attributesMapping:
                return INTEGRATION_TASKS_BY_TYPES[INTEGRATION_TASK_TYPES.rostering]
                    // .filter((taskType: string) =>
                    //     (taskType !== 'Calendar')
                    //     || (dataSourceType !== DataSourceType.Veracross)
                    //     || !dataSourceConfig
                    //     || (dataSourceConfig && ((<VeracrossConfig>dataSourceConfig).version !== VERACROSS_CONFIG_API_VERSIONS.v3)))
                    .concat(
                        INTEGRATION_TASKS_BY_TYPES[INTEGRATION_TASK_TYPES.gradebook],
                        INTEGRATION_TASKS_BY_TYPES[INTEGRATION_TASK_TYPES.resource],
                    );
            case PAGES_WHERE_INTEGRATION_TASK_ARE_SHOWN.dataRequirements:
                return INTEGRATION_TASKS_BY_TYPES[INTEGRATION_TASK_TYPES.rostering]
                    // .filter((taskType: string) =>
                    //     (taskType !== 'Calendar')
                    //     || (dataSourceType !== DataSourceType.Veracross)
                    //     || !dataSourceConfig
                    //     || (dataSourceConfig && ((<VeracrossConfig>dataSourceConfig).version !== VERACROSS_CONFIG_API_VERSIONS.v3)))
                    .concat(
                        INTEGRATION_TASKS_BY_TYPES[INTEGRATION_TASK_TYPES.gradebook],
                        INTEGRATION_TASKS_BY_TYPES[INTEGRATION_TASK_TYPES.resource],
                    );
            case PAGES_WHERE_INTEGRATION_TASK_ARE_SHOWN.filterRules:
                return INTEGRATION_TASKS_BY_TYPES[INTEGRATION_TASK_TYPES.rostering]
                    .filter((taskType: string) =>
                        (taskType !== 'School')
                        // && ((taskType !== 'Calendar')
                        //     || (dataSourceType !== DataSourceType.Veracross)
                        //     || !dataSourceConfig
                        //     || (dataSourceConfig && ((<VeracrossConfig>dataSourceConfig).version !== VERACROSS_CONFIG_API_VERSIONS.v3)))
                    )
                    .concat(
                        INTEGRATION_TASKS_BY_TYPES[INTEGRATION_TASK_TYPES.gradebook],
                        INTEGRATION_TASKS_BY_TYPES[INTEGRATION_TASK_TYPES.resource],
                    );
            case PAGES_WHERE_INTEGRATION_TASK_ARE_SHOWN.domainOfUniqueness:
                return INTEGRATION_TASKS_BY_TYPES[INTEGRATION_TASK_TYPES.rostering]
                    // .filter((taskType: string) =>
                    //     (taskType !== 'Calendar')
                    //     || (dataSourceType !== DataSourceType.Veracross)
                    //     || !dataSourceConfig
                    //     || (dataSourceConfig && ((<VeracrossConfig>dataSourceConfig).version !== VERACROSS_CONFIG_API_VERSIONS.v3))
                    // )
                    .concat(
                        INTEGRATION_TASKS_BY_TYPES[INTEGRATION_TASK_TYPES.gradebook],
                        INTEGRATION_TASKS_BY_TYPES[INTEGRATION_TASK_TYPES.resource],
                    );
            case PAGES_WHERE_INTEGRATION_TASK_ARE_SHOWN.masterFields:
                return INTEGRATION_TASKS_BY_TYPES[INTEGRATION_TASK_TYPES.rostering]
                    .filter((taskType: string) =>
                        (taskType !== 'School')
                        && (taskType !== 'Group')
                        && (taskType !== ROSTER_STUDENT_ENROLLMENT)
                        // && ((taskType !== 'Calendar')
                        //     || (dataSourceType !== DataSourceType.Veracross)
                        //     || !dataSourceConfig
                        //     || (dataSourceConfig && ((<VeracrossConfig>dataSourceConfig).version !== VERACROSS_CONFIG_API_VERSIONS.v3)))
                    )
                    .concat(
                        INTEGRATION_TASKS_BY_TYPES[INTEGRATION_TASK_TYPES.gradebook],
                        INTEGRATION_TASKS_BY_TYPES[INTEGRATION_TASK_TYPES.resource],
                    );
            default:
                return [];
        }
    }

    static isEditOrgsMappingAllowed(dataSourceConfig: DataSourceConfig, orgsMapping: OrgMapping[]): boolean {
        return !ApplicationSettingsService.isCourseraResellerConfiguration(dataSourceConfig, orgsMapping);
    }

    static isCourseraResellerConfiguration(dataSourceConfig: DataSourceConfig, orgsMapping: OrgMapping[]) {
        if (dataSourceConfig.dataSourceType === DataSourceType.Coursera) {
            return (EDIT_DELIVERY_CONFIG_AND_ORGS_MAPPING_NOT_ALLOWED_COURSERA_CLIENT_ID === (<CourseraConfig>dataSourceConfig).clientId)
                && orgsMapping.some((orgMapping: OrgMapping) => orgMapping.existing);
        }
        return false;
    }

    static removeFormGroupFromParentFormArray(childFormGroup: UntypedFormGroup) {
        let formGroupIndexToBeDeleted = -1;

        if ((<UntypedFormArray>childFormGroup?.parent)?.controls.length > 0) {
            (<UntypedFormArray>childFormGroup.parent).controls.forEach((formGroup: UntypedFormGroup, formGroupIndex: number) => {
                if (childFormGroup === formGroup) {
                    formGroupIndexToBeDeleted = formGroupIndex;
                }
            });

            if (formGroupIndexToBeDeleted > -1) {
                (<UntypedFormArray>childFormGroup.parent).removeAt(formGroupIndexToBeDeleted);
            }
        }
    }

    static isGoogleClassroomApp(application: Application) {
        return application && (application.dataSourceType === DataSourceType.GoogleClassroom);
    }

    static isGoogleClassroomSyncApp(application: Application) {
        return ApplicationSettingsService.isGoogleClassroomApp(application)
               && application.modes
               && application.modes.length > 0
               && application.modes.includes(IntegrationMode.SYNC);
    }

    // Allow Google Classroom Export mode only for organizations where host !== 'coursera.gg4l.com' || 'qwiklabs.gg4l.com'
    static filterApplicationModesForGoogleClassroom(applications: Application[], organizationHost: string): Application[] {
        const isGoogleClassroomReloadCoursesFeatureActive = (organizationHost === HOST_COURSERA)
            || (organizationHost === HOST_QWIKLABS);
        const isGoogleClassroomImportDisabled = (organizationHost === HOST_COURSERA)
            || (organizationHost === HOST_QWIKLABS);
        if (isGoogleClassroomReloadCoursesFeatureActive || isGoogleClassroomImportDisabled) {
            applications = applications.map((app: Application) => {
                if (app.dataSourceType === DataSourceType.GoogleClassroom) {
                    app.modes = app.modes.filter((mode: string) =>
                        (isGoogleClassroomReloadCoursesFeatureActive && (mode !== <string>IntegrationMode.EXPORT))
                        && (isGoogleClassroomImportDisabled && (mode !== <string>IntegrationMode.IMPORT)));
                }
                return app;
            });
        }
        return applications;
    }

    static assignIntegrationTasks(integration: Integration, newTasks: IntegrationTask[], applicationGuid: string): IntegrationTask[] {
        if (integration.source && integration.tasks) {

            integration.tasks = integration.tasks
                .map((task: IntegrationTask) => {
                    const updatedTask = newTasks.find((newTask: IntegrationTask) =>
                        (newTask.taskId === task.taskId)
                        || (!applicationGuid
                            && newTask.csvFileName === task.csvFileName
                            && newTask.sourceType === task.sourceType
                            && newTask.targetType === task.targetType)
                    );
                    return ApplicationSettingsService.getExtendedIntegrationTask(
                        Object.assign(
                            {},
                            task,
                            updatedTask
                        ),
                        integration
                    );
                })
                .filter((task: IntegrationTask) => task.taskId);

            let updatedTasks: IntegrationTask[] = [];
            if (newTasks.length > 0) {
                updatedTasks = newTasks
                    .filter((newTask: IntegrationTask) =>
                        integration.tasks.every((oldTask: IntegrationTask) => newTask.taskId !== oldTask.taskId)
                    )
                    .map((newTask: IntegrationTask) =>
                        ApplicationSettingsService.getExtendedIntegrationTask(newTask, integration)
                    );

                if (updatedTasks.length > 0) {
                    integration.tasks = integration.tasks.concat(updatedTasks);
                }
            }
        }
        return integration.tasks;
    }

    static getFilterRuleMetadata(taskId: string, filterRulesMetadata: FilterRuleMetadata[]): FilterRuleMetadata {
        return filterRulesMetadata.find((item: FilterRuleMetadata) => item.taskIdList.indexOf(taskId) > -1);
    }

    static assignFiltersMetadata(
        filtrations: RequestedFilterViewModel[], filterRulesMetadata: FilterRuleMetadata[]
    ): RequestedFilterViewModel[] {
        return filtrations
            ? filtrations.map((filter: RequestedFilterViewModel) => {
                const filterRuleMetadata = ApplicationSettingsService.getFilterRuleMetadata(filter.taskId, filterRulesMetadata);

                return Object.assign(filter, {metadata: filterRuleMetadata});
            })
            : [];
    }

    static needToUpdateIntegrationTasksData(oldIntegration: Integration, newIntegration: Integration): boolean {
        return !!oldIntegration?.integrationId
            && oldIntegration?.tasks?.every((task: IntegrationTask) => task?.sourceAttributeList?.length === 0)
            && oldIntegration?.tasks?.every((task: IntegrationTask) => task?.targetAttributeList?.length === 0)
            && ((newIntegration?.tasks?.some((task: IntegrationTask) => task?.sourceAttributeList?.length > 0))
                || (newIntegration?.tasks?.some((task: IntegrationTask) => task?.targetAttributeList?.length > 0)));
    }

    static isTestConnectionForDebugEnabled(dataSourceConfig: DataSourceConfig): boolean {
        return false && [
            DataSourceType.Aeries,
            DataSourceType.GoogleClassroom,
            DataSourceType.GSuite,
            DataSourceType.ManageBac,
            DataSourceType.OpenApply,
            DataSourceType.Qwiklabs,
            DataSourceType.Veracross,
            DataSourceType.PowerSchool,
            DataSourceType.Skyward,
            DataSourceType.Illuminates,
            DataSourceType.Coursera,
            DataSourceType.LTI
        ].includes(dataSourceConfig.dataSourceType);
    }

    static getContextIdTestConnectionForDebug(dataSourceConfig: DataSourceConfig): string {
        switch (dataSourceConfig.dataSourceType) {
            case DataSourceType.Aeries:
                return (<AeriesConfig>dataSourceConfig).certificate;
            case DataSourceType.GoogleClassroom:
                return (<GoogleClassroomConfig>dataSourceConfig).profile?.primaryEmail;
            case DataSourceType.GSuite:
                return (<GSuiteConfig>dataSourceConfig).profile?.primaryEmail;
            case DataSourceType.ManageBac:
                return (<ManageBacConfig>dataSourceConfig).apiKey;
            case DataSourceType.OpenApply:
                return (<OpenApplyConfig>dataSourceConfig).apiKey;
            case DataSourceType.Qwiklabs:
                return (<QwiklabsConfig>dataSourceConfig).baseUrl;
            case DataSourceType.Veracross:
                return (<VeracrossConfig>dataSourceConfig).username;
            case DataSourceType.PowerSchool:
                return (<PowerSchoolConfig>dataSourceConfig).clientId;
            case DataSourceType.Skyward:
                return (<SkywardConfig>dataSourceConfig).clientId;
            case DataSourceType.Illuminates:
                return (<IlluminatesConfig>dataSourceConfig).clientId;
            case DataSourceType.Coursera:
                return (<CourseraConfig>dataSourceConfig).clientId;
            case DataSourceType.LTI:
                return (<LTIConfig>dataSourceConfig).clientId;
            default:
                return (<any>dataSourceConfig)?.clientId || '';
        }
    }

    static splitLoginCredentialsPatternTemplateIntoSlices(template: string, attributesList: string[]): string[] {
        const list: string[] = template.split(/([}{])/);

        return list.map((value: string, index: number) => {
            if ((list[index - 1] === '{') && (list[index + 1] === '}')) {

                const [attribute, formulaWithArgumentsInParentheses, formulaTitle, argumentsList] =
                    this.getParsedFormulaData(value, attributesList);

                if (attribute && !formulaWithArgumentsInParentheses) {
                    // attribute
                    return `{${value}}`;
                } else {
                    if (formulaWithArgumentsInParentheses) {
                        // formula
                        return `{${formulaWithArgumentsInParentheses}}`;
                    }
                }

            } else {

                if (!!value
                    && (value !== '{')
                    && (value !== '}')) {
                    // custom value
                    return value;
                }
            }
            return '';
        })
            .filter((value: string) => !!value);
    }

    static getParsedFormulaData(value: string, attributesList: string[]): [string, string, string, string[]] {

        let attribute: string;
        let formulaWithArgumentsInParentheses = '';
        let formulaTitle: string;
        let argumentsList: string[];
        let argumentsInParentheses: string;


        if (attributesList.includes(`{${value}}`)) {
            // attribute
            attribute = `${value}`;
        } else {

            // formula
            const openParenthesesIndexOf = value.indexOf('(');
            if (openParenthesesIndexOf > -1) {
                formulaTitle = (openParenthesesIndexOf > -1) ? value.slice(0, openParenthesesIndexOf) : '';
                const isFormulaTitleCorrect = FormulaOperations
                    .some((formula: LoginCredentialsFormula) => formula.title === formulaTitle);
                if (!isFormulaTitleCorrect) {
                    formulaTitle = '';
                }

                argumentsInParentheses = value.slice(openParenthesesIndexOf);
            }

            if (formulaTitle
                && argumentsInParentheses.startsWith('(')
                && argumentsInParentheses.endsWith(')')) {
                argumentsList = argumentsInParentheses.slice(1, -1).split(',');

                formulaWithArgumentsInParentheses = `${value}`;
            }
        }

        return [attribute, formulaWithArgumentsInParentheses, formulaTitle, argumentsList];
    }

    static getIsGradeBookSyncFeatureToggleAutoTurnedOnByDefault(
        allowedApplications: Application[],
        application: Application,
        integrationMode: IntegrationMode,
        isProvider: boolean
    ) {
        const gradeBookSyncFeatureToggleAutoTurnedOnDefaultAppsList: string[] = (allowedApplications || [])
            .filter((app: Application) =>
                GRADEBOOK_SYNC_FEATURE_TOGGLE_AUTO_TURNED_ON_SYNC_APPS.includes(<DataSourceFriendlyName>app.name)
            )
            .map((app: Application) => app.guid);

        const isGradeBookSyncFeatureToggleAutoTurnedOnByDefault =
            (gradeBookSyncFeatureToggleAutoTurnedOnDefaultAppsList.includes(application?.defaultApplicationGuid))
            && (integrationMode === IntegrationMode.SYNC)
            && isProvider;
        return isGradeBookSyncFeatureToggleAutoTurnedOnByDefault;
    }

    static getIsMappingToRootBlackboardSchoolsMapping(organizationsMappingModel: OrganizationsMapping): boolean {

        // if (organizationsMappingModel?.mappingList?.length === 0) {
        //     const isMappingToRoot = (organizationsMappingModel?.sourceOrganizations?.length === 1)
        //         && (organizationsMappingModel?.sourceOrganizations[0]?.parentId === null);
        //
        //     return isMappingToRoot;
        // } else {
        const isMappingToNodes = organizationsMappingModel?.mappingList.some((orgMapping: OrgMapping) => {
            const sourceOrganization: OrgItem = organizationsMappingModel.sourceOrganizations
                .find((org: OrgItem) => org.identifier === orgMapping?.source?.identifier);
            return sourceOrganization?.parentId;
        });

        return !isMappingToNodes;
        // }
    }

    static checkIsLDAPIDPActivated(applications: Application[]) {
        const ldapApps = applications.filter((app: Application) =>
            app.configurationStep === APP_LAST_STEP
            && app.dataSourceType === DataSourceType.LDAP
        );
        return ldapApps.length > 0;
    }

    static getLongSyncScheduleDescription(toggleText: string, cronConfig: CronConfig, decimalPipe: DecimalPipe): string {
        return `${toggleText} process will work on` + ApplicationSettingsService.getShortLongSyncScheduleDescription(cronConfig, decimalPipe);
    }

    static getShortLongSyncScheduleDescription(cronConfig: CronConfig, decimalPipe: DecimalPipe): string {
        let syncScheduleDescription = '';
        const weekDays = DaysOfWeekLongFriendly;
        const monthDays = MonthDays;

        if (cronConfig.cronOption === CronOptionType.DAILY) {
            syncScheduleDescription += ' a daily basis';
        } else if ((cronConfig.cronOption === CronOptionType.WEEKLY) && cronConfig.daysOfWeek?.length > 0) {
            syncScheduleDescription += ' a weekly basis';
            const daysOfWeek = cronConfig.daysOfWeek
                .map((dayOfWeek: number) => weekDays[dayOfWeek]);
            const daysSeparator = (daysOfWeek?.length === 2) ? ' and ' : ', ';
            syncScheduleDescription += ' each ' + daysOfWeek.join(daysSeparator);
        } else if ((cronConfig.cronOption === CronOptionType.MONTHLY) && cronConfig.daysOfMonth?.length > 0) {
            syncScheduleDescription += ' a monthly basis';
            const daysOfMonth = cronConfig.daysOfMonth
                .map((dayOfMonth: number) => monthDays[dayOfMonth]);
            const daysSeparator = (daysOfMonth?.length === 2) ? ' and ' : ', ';
            syncScheduleDescription += ' each ' + daysOfMonth.join(daysSeparator);
        } else {
            syncScheduleDescription += ' a daily basis';
        }

        const hours = cronConfig.hours;
        const hoursAndMinutes = hours
            .map((hour: number) =>
                decimalPipe.transform(hour, '2.0-0')
                + ':'
                + decimalPipe.transform(cronConfig.minute, '2.0-0'));
        const hoursSeparator = (hours?.length === 2) ? ' and ' : ', ';
        syncScheduleDescription += ' at ' + hoursAndMinutes.join(hoursSeparator);

        return syncScheduleDescription;
    }

    constructor(private http: HttpClient,
                public snackBar: MatSnackBar,
                private snackBarService: SnackBarService,
                private store: Store<fromApplicationSettings.State>,
                private auditUserActivityService: AuditUserActivityService,
                private integrationService: IntegrationService) {
    }

    ngOnInit() {
    }

    /*
    * Application
    * */
    createOrUpdateApplication(data: Application): Observable<Application> {
        return this.http.post<Application>(`${environment.datahubBaseURI}/api/v2/applications`, data);
    }

    public getApplication(applicationGuid: string): Observable<Application> {
        return this.http.get<Application>(`${environment.datahubBaseURI}/api/v2/applications/${applicationGuid}`);
    }


    public getDefaultApplications(): Observable<Application[]> {
        return this.http.get<Application[]>(`${environment.datahubBaseURI}/api/v2/applications/default`);
    }

    public bindDataSourceToApplication(application: Application): Observable<Application> {
        return this.http.post<Application>(`${environment.datahubBaseURI}/api/v2/applications`, application);
    }

    /*
    * Data Source Config
    * */
    public getDataSourceConfig(dataSourceType: DataSourceType, dname: string): Observable<DataSourceConfig> {
        const dsConfigPath = DataSourcePathMap.get(dataSourceType) || DataSourceType.SFTP.toLowerCase();
        return this.http.get<DataSourceConfig>(
            `${environment.datahubBaseURI}/api/v2/applications/${dsConfigPath}/configuration?dname=${dname}`);
    }

    public updateDataSourceConfig(dataSourceType: DataSourceType, objectId: number, data: DataSourceConfig): Observable<DataSourceConfig> {
        const dsConfigPath = DataSourcePathMap.get(dataSourceType);
        return this.http.put<DataSourceConfig>(
            `${environment.datahubBaseURI}/api/v2/applications/${dsConfigPath}/configuration/${objectId}`,
            data);
    }

    public createDataSourceConfig(dataSourceType: DataSourceType, data: DataSourceConfig): Observable<DataSourceConfig> {
        const dsConfigPath = DataSourcePathMap.get(dataSourceType) || DataSourceType.SFTP.toLowerCase();
        return this.http.post<DataSourceConfig>(`${environment.datahubBaseURI}/api/v2/applications/${dsConfigPath}/configuration`, data);
    }

    public deleteDataSourceConfig(dataSourceType: DataSourceType, dname: string): Observable<any> {
        const params = new HttpParams().append('dname', dname);
        const dsConfigPath = DataSourcePathMap.get(dataSourceType);
        return this.http.delete(`${environment.datahubBaseURI}/api/v2/applications/${dsConfigPath}/configuration`,
            {params: params, responseType: 'text' as const});
    }

    public saveMappingRulesArchiveOU(
        dataSourceType: DataSourceType,
        dataSourceObjectId: number,
        archiveOU: string,
        orgGuid: string,
    ): Observable<DataSourceConfig> {
        const params = new HttpParams()
            .append('objectId', dataSourceObjectId)
            .append('archiveOu', archiveOU)
            .append('orgGuid', orgGuid);
        const dsConfigPath = DataSourcePathMap.get(dataSourceType);
        return this.http.put<DataSourceConfig>(
            `${environment.datahubBaseURI}/api/v2/applications/${dsConfigPath}/configuration/archive-ou`,
            params);
    }

    public createSharedPool(createSharedPoolData: SharedPoolResponseData): Observable<SharedPoolResponseData> {
        const vendorOrgGuid = createSharedPoolData.vendorOrgGuid;
        delete createSharedPoolData.vendorOrgGuid;

        return this.http.put<SharedPoolResponseData>(`${environment.datahubBaseURI}/api/v2/applications/shared_pools/${vendorOrgGuid}`,
            createSharedPoolData);
    }

    public getSharedPoolsByApp(applicationGuid: string): Observable<SharedPoolResponseData[]> {
        return this.http.get<SharedPoolResponseData[]>(`${environment.datahubBaseURI}/api/v2/applications/shared_pools/${applicationGuid}`);
    }

    public generateSecretKey(dataSourceConfig: DataSourceConfig): Observable<{encryptSecretKeyTimestamp: Date; encryptSecretKey: string}> {

        const dsConfigPath = DataSourcePathMap.get(dataSourceConfig.dataSourceType) || DataSourceType.SFTP.toLowerCase();
        return this.http.post<{encryptSecretKeyTimestamp: Date; encryptSecretKey: string}>(
            `${environment.datahubBaseURI}/api/v2/applications/${dsConfigPath}/configuration/generateSecretKey`,
            dataSourceConfig);
    }

    public generateLTICredentials(dataSourceConfig: DataSourceConfig): Observable<DataSourceConfig> {
        const dsConfigPath = DataSourcePathMap.get(dataSourceConfig.dataSourceType) || DataSourceType.LTI.toLowerCase();
        return this.http.post<DataSourceConfig>(
            `${environment.datahubBaseURI}/api/v2/applications/${dsConfigPath}/configuration/generate-keys`,
            dataSourceConfig);
    }

    public testConnection(dataSourceType: DataSourceType, data: DataSourceConfig): Observable<HttpResponse<string>> {
        const dsConfigPath = DataSourcePathMap.get(dataSourceType) || DataSourceType.SFTP.toLowerCase();
        return this.http.post<string>(
            `${environment.datahubBaseURI}/api/v2/applications/${dsConfigPath}/configuration/testconnection`,
            data,
            {observe: 'response'}
        );
    }

    public testETXLDAPConnection(lDAPConfig: LDAPConfig): Observable<HttpResponse<string>> {
        return this.http.post<string>(
            `${environment.etxBaseURI}/ldap/testconnection`,
            lDAPConfig,
            {observe: 'response'}
        );
    }

    public testConnectionForDebug(dataSourceType: DataSourceType, data: DataSourceConfig): Observable<HttpResponse<string>> {
        const dsConfigPath = DataSourcePathMap.get(dataSourceType) || DataSourceType.SFTP.toLowerCase();
        return this.http.post<string>(
            `${environment.datahubBaseURI}/api/v2/applications/${dsConfigPath}/configuration/testconnectionfordebug`,
            data,
            {observe: 'response'}
        );
    }

    public getTestConnectionForDebugAuditHTTPCalls(districtId: string, contextId: string): Observable<HTTPRequestDataViewModel[]> {
        return this.http.get<HTTPRequestDataViewModel[]>(
            `${environment.datahubBaseURI}/api/v2/audit/http-calls/${districtId}/${contextId}`);
    }

    public testConnectionWebhook(webhook: Webhook): Observable<HttpResponse<string>> {
        return this.http.post<string>(
            `${environment.datahubBaseURI}/api/v2/applications/webhook/testconnection`,
            webhook,
            {observe: 'response'}
        );
    }

    public deleteTestConnectionForDebugAuditHTTPCalls(districtId: string, contextId: string): Observable<string> {
        return this.http.delete<string>(`${environment.datahubBaseURI}/api/v2/audit/http-calls/${districtId}/${contextId}`);
    }

    public checkWellKnownEndPoints(hostTemplate: string): Observable<any> {
        if ((hostTemplate === HOST_COURSERA) || (hostTemplate === HOST_QWIKLABS)) {
            return this.http.get<any>(`https://${hostTemplate}/ims/lti/.well-known/jwks.json`);
        } else {
            return throwError('Host is not allowed');
        }
    }

    public saveCourseraCallbackCode(code: string, dname: string): Observable<any> {
        const url = `${environment.datahubBaseURI}/api/v2/applications/coursera/configuration/callback?dname=${dname}&code=${code}`;
        return this.http.post(url, {code: code})
            .pipe(catchError(error => this.handleError(error, MESSAGES.UNKNOWN_ERROR)));
    }

    public getGoogleAuthUrl(dataSourceType: DataSourceType, mode: IntegrationMode): Observable<any> {
        const headers = new HttpHeaders({
            'Content-Type':  'text/html'
        });
        const dsConfigPath = DataSourcePathMap.get(dataSourceType);

        return this.http.get(`${environment.datahubBaseURI}/api/v2/applications/${dsConfigPath}/configuration/authUrl/${mode}`, {
            headers: headers,
            responseType: 'text' as const
        });
    }

    public saveGoogleCallbackCode(googleClassroomCode: string, objectId: string, dsConfigPath: string): Observable<any> {
        const options = {params: new HttpParams()
            .append('objectId', objectId)
            .append('code', googleClassroomCode)
        };

        return this.http.post<any>(`${environment.datahubBaseURI}/api/v2/applications/${dsConfigPath}/configuration/authorize`,
            null, options);
    }

    public revokeAccess(objectId: number, dataSourceType: DataSourceType): Observable<any> {
        const dsConfigPath = DataSourcePathMap.get(dataSourceType);
        const url = `${environment.datahubBaseURI}/api/v2/applications/${dsConfigPath}/configuration/revokeaccess/${objectId}`;
        return this.http.get(url);
    }

    public getBlackboardApplicationId(): Observable<any> {
        const url = `${environment.datahubBaseURI}/api/v2/applications/blackboard/configuration/application_id`;
        return this.http.get(url, {responseType: 'text' as const});
    }

    handleError(error, operation?: string) {
        const errorMessage = operation || 'Something bad happened, please try again later.';
        if (error instanceof HttpErrorResponse) {
            console.log(`status: ${error.status}, ${error.statusText}`);
        } else {
            console.log(`${errorMessage}:`, error);
        }
        return throwError(errorMessage);
    }

    /*
    * Integration
    * */
    public getConfiguredEdutoneDataSources(): Observable<DataSourceConfig[]> {
        return this.http.get<DataSourceConfig[]> (`${environment.datahubBaseURI}/api/v2/applications/configured`);
    }

    public createIntegration(applicationGuid: string, integrationState: string, body: Integration): Observable<Integration> {
        const url = applicationGuid
            ? `${environment.datahubBaseURI}/api/v2/applications/${applicationGuid}/integrations/${integrationState.toUpperCase()}`
            : `${environment.datahubBaseURI}/api/v2/applications/integrations/${integrationState.toLowerCase()}`;

        return this.http.post<Integration>(url, body);
    }

    public getIntegrationData(
        applicationGuid: string, ownerGuid: string, isVendorApplication: boolean, withAttributes: boolean = true
    ): Observable<Integration> {

        let url: string;
        if (isVendorApplication) {
            url = `${environment.datahubBaseURI}/api/v2/applications/${applicationGuid}/integrations/${IntegrationState.DRAFT.toLowerCase()}`;

            return this.http.get<Integration>(url)
                .pipe(
                    map((integration: Integration) =>
                        this.integrationService.applySpecificIntegrationFixes(integration)
                    )
                );
        } else {
            const params = new HttpParams().append('withAttributes', withAttributes);
            const options = {params: params};

            url = `${environment.datahubBaseURI}/api/v2/applications/${applicationGuid}/integrations/current?ownerGuid=${ownerGuid}`;
            return this.http.get<Integration>(url, options).pipe(
                map((integration: Integration) => {
                    if (integration?.dictionaries) {
                        integration.dictionaries = integration.dictionaries.filter((dictionary: DictionaryModel) =>
                            !dictionary?.dictionaryName?.startsWith('deleted_')
                        );
                    }
                    return integration;
                })
            );
        }
    }

    public getDefaultIntegration(dataSourceType: DataSourceType, defaultSchemaName?: string): Observable<Integration> {
        const options = defaultSchemaName
            ? {
                params: new HttpParams()
                    .append('dataSourceType', dataSourceType)
                    .append('defaultSchemaName', defaultSchemaName)
            }
            : {params: new HttpParams().append('dataSourceType', dataSourceType)};
        const url = `${environment.datahubBaseURI}/api/v2/applications/integrations/default`;

        return this.http.get<Integration>(url, options)
            .pipe(catchError(error => this.handleError(error, MESSAGES.UNKNOWN_ERROR)));
    }

    public saveIntegrationTask(integrationId: string, task: IntegrationTask): Observable<IntegrationTask | {}> {
        if (task.taskId) {
            return this.http.put<IntegrationTask>(
                `${environment.datahubBaseURI}/api/v2/applications/integrations/${integrationId}/tasks/${task.taskId}`, task);
        } else {
            return this.http.post<IntegrationTask>(
                `${environment.datahubBaseURI}/api/v2/applications/integrations/${integrationId}/tasks`, task)
                .pipe(
                    map((integrationTask: IntegrationTask) => integrationTask),
                    catchError((error) => {

                        if (error.status === 406) {
                            return observableOf(error);
                        } else {
                            const message = this.SAVE_INTEGRATION_ENTITY_MAPPING_ERROR_MESSAGE;
                            this.snackBarService.showSnackBarMessage(message, 3000);
                            return observableOf(new Error(message));
                        }
                    }));
        }
    }

    public deleteIntegration(integrationId: string): Observable<any> {
        return this.http.delete<any>(
            `${environment.datahubBaseURI}/api/v2/applications/integrations/${integrationId}`)
            .pipe(
                map((response: any) => response),
                catchError((error) => {
                    if (error.status === 423) {
                        this.snackBar.open(this.SAVE_EDIT_INTEGRATION_ERROR_MESSAGE, '', <MatSnackBarConfig>{duration: 3000});
                    } else {
                        if ((error.status === 409) && error.error) {
                            this.snackBar.open(error.error, '', <MatSnackBarConfig>{duration: 3000});
                        } else {
                            this.snackBar.open(MESSAGES.UNKNOWN_ERROR, '', <MatSnackBarConfig>{duration: 3000});
                        }
                    }
                    return throwError(error);
                }
                ));
    }

    public changeCSVFileNameOfIntegrationTask(integrationId: string, taskId: string, csvFileName: string): Observable<IntegrationTask> {
        return this.http.put<IntegrationTask>(
            `${environment.datahubBaseURI}/api/v2/applications/integrations/${integrationId}/tasks/${taskId}/${csvFileName}`, null);
    }

    public deleteIntegrationTask(integrationId: string, taskId: string): Observable<any> {
        return this.http.delete<any>(
            `${environment.datahubBaseURI}/api/v2/applications/integrations/${integrationId}/tasks/${taskId}`);
    }

    /*
    * FTP Config
    * */
    public createFTPUser(ftpUser: FTPUser, orgGuid: string): Observable<FTPUser> {
        return this.createOrUpdateFTPUser(ftpUser, orgGuid);
    }

    public updateFTPUser(ftpUser: FTPUser, orgGuid: string, oldUsername: string): Observable<FTPUser> {
        return this.createOrUpdateFTPUser(ftpUser, orgGuid, oldUsername);
    }

    private createOrUpdateFTPUser(ftpUser: FTPUser, orgGuid: string, oldUsername: string = null): Observable<FTPUser> {
        const formData = new FormData();
        const keyFile = null;
        formData.append('ftp_key', keyFile);
        formData.append('userPermission', new Blob([JSON.stringify(ftpUser)], {type: 'application/json'}));
        const URL = `${environment.datahubBaseURI}/api/v2/ftp/config/permission/save?dzOrgId=${orgGuid}`
            + (oldUsername ? `&oldUsername=${oldUsername}` : '');
        return this.http.post<FTPUser>(URL, formData);
    }

    public getFTPConfig(orgGuid: string): Observable<FTPConfig> {
        return this.http.get<FTPConfig>(`${environment.datahubBaseURI}/api/v2/ftp/config?dzOrgId=${orgGuid}`);
    }

    public deleteFTPUser(username: string, orgGuid: string) {
        return this.http.delete(
            `${environment.datahubBaseURI}/api/v2/ftp/config/permission/delete?dzOrgId=${orgGuid}&username=${username}`
        );
    }

    /*
     * Integration Org Mapping
     * */

    public getIntegrationOrgMapping(integrationId: string): Observable<OrganizationsMapping> {
        return this.http.get<OrganizationsMapping>(
            `${environment.datahubBaseURI}/api/v2/applications/integrations/${integrationId}/organizations/mapping`);
    }

    public saveIntegrationOrgMapping(integrationId: string, orgsMapping: OrgMapping[]): Observable<OrganizationsMapping> {
        return this.http.post<OrganizationsMapping>(
            `${environment.datahubBaseURI}/api/v2/applications/integrations/${integrationId}/organizations/mapping/bulk`, orgsMapping);
    }

    public getIntegrationOrgMappingCurrent(integrationId: string): Observable<OrganizationsMapping> {
        return this.http.get<OrganizationsMapping>(
            `${environment.datahubBaseURI}/api/v2/applications/integrations/${integrationId}/organizations/mapping/current`);
    }

    public getIntegrationRules(integrationId: string): Observable<IntegrationRulesMap> {
        return this.http.get<IntegrationRulesMap>(
            `${environment.datahubBaseURI}/api/v2/applications/integrations/${integrationId}/rules`);
    }

    public saveIntegrationRules(integrationId: string, param: string, rules: EntityFilterModel[]): Observable<EntityFilterModel[]> {
        return this.http.put<EntityFilterModel[]>(
            `${environment.datahubBaseURI}/api/v2/applications/integrations/${integrationId}/rules/${param}`,
            rules);
    }

    /*
     * Integration Process config
     * */
    public saveIntegrationProcessConfig(integrationId: string, data: ProcessConfig[]): Observable<ProcessConfig[]> {
        return this.http
            .put<ProcessConfig[]>(`${environment.datahubBaseURI}/api/v2/applications/integrations/${integrationId}/process`, data);
    }

    /*
     * Merging Rules / Domains of Uniqueness
     */
    getMergingRules(integrationId: string): Observable<DomainOfUniqueness[]> {
        return this.http.get<DomainOfUniqueness[]>(
            `${environment.datahubBaseURI}/api/v2/applications/integrations/${integrationId}/domainUniqueness`);
    }

    saveMergingRules(integrationId: string, mergingRules: DomainOfUniqueness[]): Observable<DomainOfUniqueness[]> {
        return this.http.put<DomainOfUniqueness[]>(
            `${environment.datahubBaseURI}/api/v2/applications/integrations/${integrationId}/domainUniqueness`,
            mergingRules);
    }

    getMasterFields(orgGuid: string): Observable<MasterFieldsMergingRule[]> {
        const url = `${environment.datahubBaseURI}/api/v2/applications/merge/rules?orgGuid=${orgGuid}`;
        return this.http.get<MasterFieldsMergingRule[]>(url);
    }

    saveMasterFields(masterFields: MasterFieldsMergingRule[]): Observable<MasterFieldsMergingRule[]> {
        const url = `${environment.datahubBaseURI}/api/v2/applications/merge/rules/bulk`;
        return this.http.post<MasterFieldsMergingRule[]>(url, masterFields);
    }

    deleteMasterFields(masterFieldsIdList: string[]): Observable<any> {
        const url = `${environment.datahubBaseURI}/api/v2/applications/merge/rules/bulk`;
        return this.http.delete(url, {body: masterFieldsIdList});
    }

    saveMasterField(field: MasterFieldsMergingRule): Observable<MasterFieldsMergingRule[]> {
        const url = `${environment.datahubBaseURI}/api/v2/applications/merge/rules`;
        return this.http.post<MasterFieldsMergingRule[]>(url, field);
    }

    deleteMasterField(ruleId: string) {
        const url = `${environment.datahubBaseURI}/api/v2/applications/merge/rules/${ruleId}`;
        return this.http.delete(url);
    }

    /*
    * Webhook / Auto Provisioning
    */
    getAutoProvisioning(appGuid: string): Observable<Webhook> {
        const url = `${environment.datahubBaseURI}/api/v2/applications/${appGuid}/webhook`;
        return this.http.get<Webhook>(url);
    }

    saveAutoProvisioning(appGuid: string, webhook: Webhook): Observable<Webhook> {
        const url = `${environment.datahubBaseURI}/api/v2/applications/${appGuid}/webhook`;
        return this.http.post<Webhook>(url, webhook);
    }

    /*
     * Dictionary
     */
    public getDictionary(orgGuid: string, dname: string, dictionaryName: string): Observable<DictionaryModel> {
        const params = new HttpParams()
            .append('orgGuid', orgGuid)
            .append('dname', dname)
            .append('dictionaryName', dictionaryName);
        return this.http.get<DictionaryModel>(
            `${environment.datahubBaseURI}/api/v2/applications/qwiklabs/configuration/getDictionary`, {params: params});
    }

    public saveDictionaryByName(
        integrationId: string, entries: DictionaryEntryModel[], dictionaryName: string
    ): Observable<DictionaryEntryModel[]> {
        const params = new HttpParams().append('dictionaryName', dictionaryName);
        return this.http.put<DictionaryEntryModel[]>(
            `${environment.datahubBaseURI}/api/v2/applications/dictionary/mapping/${integrationId}`, entries, {params: params});
    }

    public resetGradeMapping(dsConfigPath: string, dname: string): Observable<DictionaryModel> {
        return this.http.post<DictionaryModel>(
            `${environment.datahubBaseURI}/api/v2/applications/${dsConfigPath}/configuration/reloadGrades`, dname);
    }

    public saveQwiklabsDictionary(
        dictionaryName: string, dname: string, entries: QwiklabsDictionaryEntryModel[]
    ): Observable<DictionaryModel> {
        const params = new HttpParams().append('dictionaryName', dictionaryName)
            .append('dname', dname);
        return this.http.put<DictionaryModel>(
            `${environment.datahubBaseURI}/api/v2/applications/qwiklabs/configuration/saveDictionary`, entries, {params: params});
    }

    public getQwiklabsCourses(dname: string, deploymentIds: string): Observable<QwiklabsCourse[]> {
        const params = new HttpParams()
            .append('dname', dname)
            .append('deploymentIds', `${deploymentIds}`);
        return this.http.get<QwiklabsCourse[]>(
            `${environment.datahubBaseURI}/api/v2/applications/qwiklabs/configuration/loadCourses`,
            {params: params});
    }

    public evictQwiklabsCourses(dname: string, deploymentIds: string): Observable<any> {
        const params = new HttpParams()
            .append('dname', dname)
            .append('deploymentIds', deploymentIds);
        return this.http.delete(`${environment.datahubBaseURI}/api/v2/applications/qwiklabs/configuration/evictLoadCourses`,
            {params: params});
    }

    public saveAppLinkDictionaries(dname: string, entries: DictionaryEntryModel[]): Observable<DictionaryModel> {
        const params = new HttpParams().append('dname', dname);
        return this.http.put<DictionaryModel>(
            `${environment.datahubBaseURI}/api/v2/applications/google-classroom/configuration/saveAppLinkDictionaries`, entries, {params: params});
    }

    public getAppLinkDictionaries(dname: string): Observable<DictionaryModel> {
        const params = new HttpParams().append('dname', dname);
        return this.http.get<DictionaryModel>(
            `${environment.datahubBaseURI}/api/v2/applications/google-classroom/configuration/getAppLinkDictionaries`,
            {params: params});
    }

    /*
     * Coursera Courses
     */
    public getCourseraPrograms(districtGuid: string): Observable<CourseraProgram[]> {
        const params = new HttpParams()
            .append('districtGuid', districtGuid);
        return this.http.get<CourseraProgram[]>(
            `${environment.datahubBaseURI}/api/v2/applications/google-classroom/configuration/courseraPrograms`, {params: params});
    }

    public getCourseraCourses(districtGuid: string, courseraProgramId: string, query: string): Observable<CourseraCourse[]> {
        const params = new HttpParams()
            .append('districtGuid', districtGuid)
            .append('programId', courseraProgramId)
            .append('searchChunk', query || '');
        return this.http.get<CourseraProgram[]>(
            `${environment.datahubBaseURI}/api/v2/applications/google-classroom/configuration/courseraCourses`, {params: params});
    }

    /*
     * Google-Classroom
     */
    public reloadGoogleClassroomCourses(dname: string): Observable<void> {
        return this.http.post<void>(
            `${environment.datahubBaseURI}/api/v2/applications/google-classroom/configuration/api/reloadCourses?dname=${dname}`, null);
    }

    public getIsLoadingGoogleClassroomCourses(dname: string): Observable<boolean> {
        return this.http.get<boolean>(
            `${environment.datahubBaseURI}/api/v2/applications/google-classroom/configuration/api/isLoadingCourses?dname=${dname}`);
    }

    /*
     * Other
     */
    public getDefaultAppByIcon(application: Application, defaultApplications: Application[] = <Application[]>[]): Application {
        return application
            && defaultApplications.find((defaultApp: Application) => application.icon === defaultApp.icon);
    }

    public getSensitiveData(orgGuid: string): Observable<SensitiveData[]> {
        const url = `${environment.datahubBaseURI}/api/v2/applications/pii/orgs/${orgGuid}/sensitive_fields`;

        return this.http.get<SensitiveData[]>(url);
    }

    /*
     * Email Relay
     */
    public getEmailRelay(orgGuid: string, appGuid: string): Observable<EmailRelay> {
        const url = `${environment.datahubBaseURI}/api/v2/organizations/${orgGuid}/applications/${appGuid}/emails/relays`;
        return this.http.get<EmailRelay>(url, {observe: 'response'})
            .pipe(
                map((resp: HttpResponse<EmailRelay>) =>
                    resp?.status === 200
                        ? resp.body
                        : new EmailRelay()
                ),
                switchMap((emailRelay: EmailRelay) =>
                    emailRelay?.email
                        ? this.manualCheckVerificationEmailRelay(emailRelay)
                        : of(emailRelay)
                ),
                catchError(error =>
                    error?.status === 404
                        ? of(new EmailRelay())
                        : this.handleError(error, MESSAGES.UNKNOWN_ERROR)
                )
            );
    }

    manualCheckVerificationEmailRelay(emailRelay: EmailRelay): Observable<EmailRelay> {
        const url = `${environment.datahubBaseURI}/api/v2/organizations/${emailRelay?.organizationGuid}/applications/${emailRelay?.applicationGuid}/emails/relays`;
        return this.http.put<EmailRelay>(url, emailRelay);
    }

    saveEmailRelay(appGuid: string, orgGuid: string, email: string): Observable<EmailRelay> {
        const url = `${environment.datahubBaseURI}/api/v2/organizations/${orgGuid}/applications/${appGuid}/emails/relays`;
        const emailRelay = new EmailRelay(
            appGuid,
            orgGuid,
            email
        );
        return this.http.post<EmailRelay>(url, emailRelay, {observe: 'response'})
            .pipe(
                map((resp: HttpResponse<EmailRelay>) => resp.body),
                catchError((error: HttpErrorResponse) => {

                    if (error?.status === 400) {
                        if (error.error === 'Invalid email format') {
                            return this.handleError(error, 'Invalid email format.');
                        }

                        if (error.error === 'Email already exists') {
                            return this.handleError(error, 'This email address is already registered.');
                        }
                    }

                    if (error?.status === 403) {
                        return this.handleError(error, 'This domain is already registered by another organization.');
                    }

                    return this.handleError(error, 'Unknown error occurred while saving outgoing email');
                })
            );
    }

    deleteEmailRelay(appGuid: string, orgGuid: string): Observable<EmailRelay> {
        const url = `${environment.datahubBaseURI}/api/v2/organizations/${orgGuid}/applications/${appGuid}/emails/relays`;
        return this.http.delete<EmailRelay>(url);
    }

    /*
    * Data Source Config
    */
    getDataSourceConfigFormData(deliveryConfigFormGroup: UntypedFormGroup, dataSourceConfig: DataSourceConfig, applicationGuid: string = null): DataSourceConfig {
        let newDataSourceConfig;
        switch (dataSourceConfig.dataSourceType) {
            case <string>DataSourceType.DataAPI:
                newDataSourceConfig = Object.assign(
                    {},
                    dataSourceConfig,
                    {
                        dataSourceType: dataSourceConfig.dataSourceType,
                        defaultSchemaName: deliveryConfigFormGroup.controls.dataApiConfigCtrl.value,
                        significantChangesMaxPercent: dataSourceConfig.significantChangesMaxPercent || 90,
                        significantUpdatePercent: deliveryConfigFormGroup.controls.significantUpdatePercentCtrl.value,
                        significantDeletePercent: deliveryConfigFormGroup.controls.significantDeletePercentCtrl.value
                    }
                );
                break;

            case <string>DataSourceType.SFTP:
                if (!dataSourceConfig.isManuallyUpload) {
                    // SFTP
                    newDataSourceConfig = this.getSFTPConfigFormData(
                        <SFTPConfig>dataSourceConfig,
                        deliveryConfigFormGroup
                    );
                } else {
                    // CSV Upload
                    newDataSourceConfig = Object.assign(
                        {},
                        dataSourceConfig,
                        {
                            dataSourceType: dataSourceConfig.dataSourceType,
                            defaultSchemaName: deliveryConfigFormGroup.controls.dataSchemaCtrl.value,
                            isCreateProviderFolder: false,
                            sisIdPrefix: deliveryConfigFormGroup.controls.sisIdPrefixCtrl.value,
                            isAllowedRemoveRosterData: deliveryConfigFormGroup.controls.isAllowedRemoveRosterDataCtrl.value,
                            isSaveDataSnapshots: !!deliveryConfigFormGroup.controls.isSaveDataSnapshotsCtrl.value,
                            isDetectDuplicatesEnabled: !!deliveryConfigFormGroup.controls.isDetectDuplicatesEnabledCtrl.value,
                            contactNamePattern: deliveryConfigFormGroup.controls.contactNamePatternCtrl.value
                                ? deliveryConfigFormGroup.controls.contactNamePatternCtrl.value.trim()
                                : '',
                            isSupportedUsersWithMultipleRoles: !!deliveryConfigFormGroup.controls.isSupportedUsersWithMultipleRolesCtrl.value,
                            significantChangesMaxPercent: dataSourceConfig.significantChangesMaxPercent || 90,
                            significantUpdatePercent: deliveryConfigFormGroup.controls.significantUpdatePercentCtrl.value,
                            significantDeletePercent: deliveryConfigFormGroup.controls.significantDeletePercentCtrl.value
                        }
                    );
                }
                ApplicationSettingsService.trimStringValuesOfObject(newDataSourceConfig);
                break;

            case <string>DataSourceType.EdFi:
                newDataSourceConfig = Object.assign(
                    {},
                    dataSourceConfig,
                    {dataSourceType: dataSourceConfig.dataSourceType},
                    {
                        baseUrl: deliveryConfigFormGroup.controls.baseUrlCtrl.value,
                        clientId: deliveryConfigFormGroup.controls.clientIdCtrl.value,
                        clientSecret: deliveryConfigFormGroup.controls.clientSecretCtrl.value,
                        sisIdPrefix: deliveryConfigFormGroup.controls.sisIdPrefixCtrl.value,
                        isAllowedRemoveRosterData: deliveryConfigFormGroup.controls.isAllowedRemoveRosterDataCtrl.value,
                        isSaveDataSnapshots: !!deliveryConfigFormGroup.controls.isSaveDataSnapshotsCtrl.value,
                        isDetectDuplicatesEnabled: !!deliveryConfigFormGroup.controls.isDetectDuplicatesEnabledCtrl.value,
                        isSupportedUsersWithMultipleRoles: !!deliveryConfigFormGroup.controls.isSupportedUsersWithMultipleRolesCtrl.value,
                        significantChangesMaxPercent: dataSourceConfig.significantChangesMaxPercent || 90,
                        significantUpdatePercent: deliveryConfigFormGroup.controls.significantUpdatePercentCtrl.value,
                        significantDeletePercent: deliveryConfigFormGroup.controls.significantDeletePercentCtrl.value
                    }
                );
                ApplicationSettingsService.trimStringValuesOfObject(newDataSourceConfig);
                break;

            case <string>DataSourceType.OneRoster:
                newDataSourceConfig = Object.assign(
                    {},
                    dataSourceConfig,
                    {dataSourceType: dataSourceConfig.dataSourceType},
                    {
                        oneRosterBaseUrl: deliveryConfigFormGroup.controls.baseUrlCtrl.value,
                        clientId: deliveryConfigFormGroup.controls.clientIdCtrl.value,
                        clientSecret: deliveryConfigFormGroup.controls.clientSecretCtrl.value,
                        vendorKey: deliveryConfigFormGroup.controls?.vendorKeyCtrl?.value,
                        vendorSecret: deliveryConfigFormGroup.controls?.vendorSecretCtrl?.value,
                        authUrl: deliveryConfigFormGroup.controls.authUrlCtrl.value,
                        sisIdPrefix: deliveryConfigFormGroup.controls.sisIdPrefixCtrl.value,
                        isAllowedRemoveRosterData: deliveryConfigFormGroup.controls.isAllowedRemoveRosterDataCtrl.value,
                        isSaveDataSnapshots: !!deliveryConfigFormGroup.controls.isSaveDataSnapshotsCtrl.value,
                        isDetectDuplicatesEnabled: !!deliveryConfigFormGroup.controls.isDetectDuplicatesEnabledCtrl.value,
                        isSupportedUsersWithMultipleRoles: !!deliveryConfigFormGroup.controls.isSupportedUsersWithMultipleRolesCtrl.value,
                        significantChangesMaxPercent: dataSourceConfig.significantChangesMaxPercent || 90,
                        significantUpdatePercent: deliveryConfigFormGroup.controls.significantUpdatePercentCtrl.value,
                        significantDeletePercent: deliveryConfigFormGroup.controls.significantDeletePercentCtrl.value
                    }
                );
                ApplicationSettingsService.trimStringValuesOfObject(newDataSourceConfig);
                break;

            case <string>DataSourceType.PowerSchool:
                const contactModuleFilter = deliveryConfigFormGroup.controls.contactModuleFilterCtrl.value
                    ? deliveryConfigFormGroup.controls.contactModuleFilterCtrl.value.sort()
                    : [];
                newDataSourceConfig = Object.assign(
                    {},
                    dataSourceConfig,
                    {dataSourceType: dataSourceConfig.dataSourceType},
                    {
                        powerSchoolBaseUrl: deliveryConfigFormGroup.controls.baseUrlCtrl.value,
                        clientId: deliveryConfigFormGroup.controls.clientIdCtrl.value,
                        clientSecret: deliveryConfigFormGroup.controls.clientSecretCtrl.value,
                        sisIdPrefix: deliveryConfigFormGroup.controls.sisIdPrefixCtrl.value,
                        contactApi: !deliveryConfigFormGroup.controls.contactApiCtrl.value,
                        contactModuleFilter: deliveryConfigFormGroup.controls.contactModuleFilterCtrl.value
                            ? deliveryConfigFormGroup.controls.contactModuleFilterCtrl.value.join()
                            : deliveryConfigFormGroup.controls.contactModuleFilterCtrl.value,
                        includeCoTeachers: deliveryConfigFormGroup.controls.includeCoTeachersCtrl.value,
                        isAllowedRemoveRosterData: deliveryConfigFormGroup.controls.isAllowedRemoveRosterDataCtrl.value,
                        isSaveDataSnapshots: !!deliveryConfigFormGroup.controls.isSaveDataSnapshotsCtrl.value,
                        isDetectDuplicatesEnabled: !!deliveryConfigFormGroup.controls.isDetectDuplicatesEnabledCtrl.value,
                        contactNamePattern: deliveryConfigFormGroup.controls.contactNamePatternCtrl.value
                            ? deliveryConfigFormGroup.controls.contactNamePatternCtrl.value.trim()
                            : '',
                        isSupportedUsersWithMultipleRoles: !!deliveryConfigFormGroup.controls.isSupportedUsersWithMultipleRolesCtrl.value,
                        significantChangesMaxPercent: dataSourceConfig.significantChangesMaxPercent || 90,
                        significantUpdatePercent: deliveryConfigFormGroup.controls.significantUpdatePercentCtrl.value,
                        significantDeletePercent: deliveryConfigFormGroup.controls.significantDeletePercentCtrl.value
                    }
                );
                ApplicationSettingsService.trimStringValuesOfObject(newDataSourceConfig);
                break;

            case <string>DataSourceType.Skyward:
                newDataSourceConfig = Object.assign(
                    {},
                    dataSourceConfig,
                    {dataSourceType: dataSourceConfig.dataSourceType},
                    {
                        baseUrl: deliveryConfigFormGroup.controls.baseUrlCtrl.value,
                        clientId: deliveryConfigFormGroup.controls.clientIdCtrl.value,
                        clientSecret: deliveryConfigFormGroup.controls.clientSecretCtrl.value,
                        sisIdPrefix: deliveryConfigFormGroup.controls.sisIdPrefixCtrl.value,
                        isAllowedRemoveRosterData: deliveryConfigFormGroup.controls.isAllowedRemoveRosterDataCtrl.value,
                        isSaveDataSnapshots: !!deliveryConfigFormGroup.controls.isSaveDataSnapshotsCtrl.value,
                        isDetectDuplicatesEnabled: !!deliveryConfigFormGroup.controls.isDetectDuplicatesEnabledCtrl.value,
                        isSupportedUsersWithMultipleRoles: !!deliveryConfigFormGroup.controls.isSupportedUsersWithMultipleRolesCtrl.value,
                        significantChangesMaxPercent: dataSourceConfig.significantChangesMaxPercent || 90,
                        significantUpdatePercent: deliveryConfigFormGroup.controls.significantUpdatePercentCtrl.value,
                        significantDeletePercent: deliveryConfigFormGroup.controls.significantDeletePercentCtrl.value
                    }
                );
                ApplicationSettingsService.trimStringValuesOfObject(newDataSourceConfig);
                break;

            case <string>DataSourceType.Inow:
                newDataSourceConfig = Object.assign(
                    {},
                    dataSourceConfig,
                    {dataSourceType: dataSourceConfig.dataSourceType},
                    {
                        baseUrl: deliveryConfigFormGroup.controls.baseUrlCtrl.value,
                        clientId: deliveryConfigFormGroup.controls.clientIdCtrl.value,
                        clientSecret: deliveryConfigFormGroup.controls.clientSecretCtrl.value,
                        applicationKey: deliveryConfigFormGroup.controls.applicationKeyCtrl.value,
                        sisIdPrefix: deliveryConfigFormGroup.controls.sisIdPrefixCtrl.value,
                        isAllowedRemoveRosterData: deliveryConfigFormGroup.controls.isAllowedRemoveRosterDataCtrl.value,
                        isSaveDataSnapshots: !!deliveryConfigFormGroup.controls.isSaveDataSnapshotsCtrl.value,
                        isDetectDuplicatesEnabled: !!deliveryConfigFormGroup.controls.isDetectDuplicatesEnabledCtrl.value,
                        isSupportedUsersWithMultipleRoles: !!deliveryConfigFormGroup.controls.isSupportedUsersWithMultipleRolesCtrl.value,
                        significantChangesMaxPercent: dataSourceConfig.significantChangesMaxPercent || 90,
                        significantUpdatePercent: deliveryConfigFormGroup.controls.significantUpdatePercentCtrl.value,
                        significantDeletePercent: deliveryConfigFormGroup.controls.significantDeletePercentCtrl.value
                    }
                );
                ApplicationSettingsService.trimStringValuesOfObject(newDataSourceConfig);
                break;

            case <string>DataSourceType.Illuminates:
                newDataSourceConfig = Object.assign(
                    {},
                    dataSourceConfig,
                    {dataSourceType: dataSourceConfig.dataSourceType},
                    {
                        baseUrl: deliveryConfigFormGroup.controls.baseUrlCtrl.value,
                        clientId: deliveryConfigFormGroup.controls.clientIdCtrl.value,
                        clientSecret: deliveryConfigFormGroup.controls.clientSecretCtrl.value,
                        sisIdPrefix: deliveryConfigFormGroup.controls.sisIdPrefixCtrl.value,
                        isAllowedRemoveRosterData: deliveryConfigFormGroup.controls.isAllowedRemoveRosterDataCtrl.value,
                        isSaveDataSnapshots: !!deliveryConfigFormGroup.controls.isSaveDataSnapshotsCtrl.value,
                        isDetectDuplicatesEnabled: !!deliveryConfigFormGroup.controls.isDetectDuplicatesEnabledCtrl.value,
                        isSupportedUsersWithMultipleRoles: !!deliveryConfigFormGroup.controls.isSupportedUsersWithMultipleRolesCtrl.value,
                        significantChangesMaxPercent: dataSourceConfig.significantChangesMaxPercent || 90,
                        significantUpdatePercent: deliveryConfigFormGroup.controls.significantUpdatePercentCtrl.value,
                        significantDeletePercent: deliveryConfigFormGroup.controls.significantDeletePercentCtrl.value
                    }
                );
                ApplicationSettingsService.trimStringValuesOfObject(newDataSourceConfig);
                break;

            case <string>DataSourceType.Coursera:
                const clientSecret = ApplicationSettingsService.getNotFakePasswordValue(
                    deliveryConfigFormGroup.controls.clientSecretCtrl.value,
                    dataSourceConfig.dname,
                    (<CourseraConfig>dataSourceConfig).clientSecret
                );
                const password = ApplicationSettingsService.getNotFakePasswordValue(
                    deliveryConfigFormGroup.controls.passwordCtrl.value,
                    dataSourceConfig.dname,
                    (<CourseraConfig>dataSourceConfig).password
                );

                newDataSourceConfig = Object.assign(
                    {},
                    dataSourceConfig,
                    {dataSourceType: dataSourceConfig.dataSourceType},
                    {
                        clientId: deliveryConfigFormGroup.controls.clientIdCtrl.value,
                        clientSecret: clientSecret,
                        orgId: deliveryConfigFormGroup.controls.orgIdCtrl.value,
                        username: deliveryConfigFormGroup.controls.usernameCtrl.value,
                        password: password,
                        granularGradeSync: deliveryConfigFormGroup.controls.granularGradeSyncCtrl.value,
                        includeDeletedMembers: deliveryConfigFormGroup.controls.includeDeletedMembersCtrl.value,
                        isGradeSyncWithoutSsoEnabled: deliveryConfigFormGroup.controls.isGradeSyncWithoutSsoEnabledCtrl.value,
                        bulkUserInvitation: deliveryConfigFormGroup.controls.bulkUserInvitationCtrl.value,
                        piiShieldEnabled: deliveryConfigFormGroup.controls.piiShieldEnabledCtrl.value,
                        invitationDisabled: deliveryConfigFormGroup.controls.invitationDisabledCtrl.value,
                        includeExpiredContracts: deliveryConfigFormGroup.controls.includeExpiredContractsCtrl.value,
                        sisIdPrefix: deliveryConfigFormGroup.controls.sisIdPrefixCtrl.value,
                        isAllowedRemoveRosterData: deliveryConfigFormGroup.controls.isAllowedRemoveRosterDataCtrl.value,
                        isSaveDataSnapshots: !!deliveryConfigFormGroup.controls.isSaveDataSnapshotsCtrl.value,
                        isDetectDuplicatesEnabled: !!deliveryConfigFormGroup.controls.isDetectDuplicatesEnabledCtrl.value,
                        isSupportedUsersWithMultipleRoles: !!deliveryConfigFormGroup.controls.isSupportedUsersWithMultipleRolesCtrl.value,
                        significantChangesMaxPercent: dataSourceConfig.significantChangesMaxPercent || 90,
                        significantUpdatePercent: deliveryConfigFormGroup.controls.significantUpdatePercentCtrl.value,
                        significantDeletePercent: deliveryConfigFormGroup.controls.significantDeletePercentCtrl.value
                    }
                );
                ApplicationSettingsService.trimStringValuesOfObject(newDataSourceConfig);
                break;

            case <string>DataSourceType.LTI:
                const isV1_3 = deliveryConfigFormGroup.controls.ltiVersionCtrl.value === LTI_CONFIG_VERSION['v.1.3'];
                newDataSourceConfig = Object.assign(
                    {},
                    dataSourceConfig,
                    {dataSourceType: dataSourceConfig.dataSourceType},
                    {
                        ltiVersion: deliveryConfigFormGroup.controls.ltiVersionCtrl.value,
                        clientId: deliveryConfigFormGroup.controls.clientIdCtrl.value,
                        baseUrl: isV1_3 ? null : deliveryConfigFormGroup.controls.baseUrlCtrl.value,
                        oidcAuthUrl: isV1_3 ? deliveryConfigFormGroup.controls.oidcAuthUrlCtrl.value : null,
                        keySetUrl: isV1_3 ? deliveryConfigFormGroup.controls.keySetUrlCtrl.value : null,
                        oauth2AccessTokenUrl: isV1_3 ? deliveryConfigFormGroup.controls.oauth2AccessTokenUrlCtrl.value : null,
                        issuer: isV1_3 ? deliveryConfigFormGroup.controls.issuerCtrl.value : null,
                        deploymentId: isV1_3 ? deliveryConfigFormGroup.controls.deploymentIdCtrl.value : null,
                        useGranularGradeSync: isV1_3 && deliveryConfigFormGroup.controls.useGranularGradeSyncCtrl.value,
                        incrementalExport: isV1_3 && deliveryConfigFormGroup.controls.incrementalExportCtrl.value,
                        exportAdditionalAttributes: isV1_3 && deliveryConfigFormGroup.controls.exportAdditionalAttributesCtrl.value,
                        autoCreateLineItems: isV1_3 && deliveryConfigFormGroup.controls.useGranularGradeSyncCtrl.value
                            && deliveryConfigFormGroup.controls.autoCreateLineItemsCtrl.value,
                        multipleLMSShells: deliveryConfigFormGroup.controls.multipleLMSShellsCtrl.value,
                        sisIdPrefix: deliveryConfigFormGroup.controls.sisIdPrefixCtrl.value,
                        isAllowedRemoveRosterData: deliveryConfigFormGroup.controls.isAllowedRemoveRosterDataCtrl.value,
                        isSaveDataSnapshots: !!deliveryConfigFormGroup.controls.isSaveDataSnapshotsCtrl.value,
                        isDetectDuplicatesEnabled: !!deliveryConfigFormGroup.controls.isDetectDuplicatesEnabledCtrl.value,
                        applicationGuid: applicationGuid,
                        isSupportedUsersWithMultipleRoles: !!deliveryConfigFormGroup.controls.isSupportedUsersWithMultipleRolesCtrl.value,
                        significantChangesMaxPercent: dataSourceConfig.significantChangesMaxPercent || 90,
                        significantUpdatePercent: deliveryConfigFormGroup.controls.significantUpdatePercentCtrl.value,
                        significantDeletePercent: deliveryConfigFormGroup.controls.significantDeletePercentCtrl.value
                    }
                );
                ApplicationSettingsService.trimStringValuesOfObject(newDataSourceConfig);
                break;

            case <string>DataSourceType.LDAP:
                newDataSourceConfig = Object.assign(
                    {},
                    dataSourceConfig,
                    {dataSourceType: dataSourceConfig.dataSourceType},
                    {
                        domain: deliveryConfigFormGroup.controls.domainCtrl.value,
                        host: deliveryConfigFormGroup.controls.hostCtrl.value,
                        port: deliveryConfigFormGroup.controls.portCtrl.value,
                        username: deliveryConfigFormGroup.controls.usernameCtrl.value,
                        password: deliveryConfigFormGroup.controls.passwordCtrl.value,
                        requireSsl: !!deliveryConfigFormGroup.controls.isRequireSslCtrl.value,
                        importGroupsAsSections: !!deliveryConfigFormGroup.controls.importGroupsAsSectionsCtrl.value,
                        sisIdPrefix: deliveryConfigFormGroup.controls.sisIdPrefixCtrl.value,
                        isAllowedRemoveRosterData: deliveryConfigFormGroup.controls.isAllowedRemoveRosterDataCtrl.value,
                        isSaveDataSnapshots: !!deliveryConfigFormGroup.controls.isSaveDataSnapshotsCtrl.value,
                        isDetectDuplicatesEnabled: !!deliveryConfigFormGroup.controls.isDetectDuplicatesEnabledCtrl.value,
                        isSupportedUsersWithMultipleRoles: !!deliveryConfigFormGroup.controls.isSupportedUsersWithMultipleRolesCtrl.value,
                        significantChangesMaxPercent: dataSourceConfig.significantChangesMaxPercent || 90,
                        significantUpdatePercent: deliveryConfigFormGroup.controls.significantUpdatePercentCtrl.value,
                        significantDeletePercent: deliveryConfigFormGroup.controls.significantDeletePercentCtrl.value
                    }
                );
                ApplicationSettingsService.trimStringValuesOfObject(newDataSourceConfig);
                break;

            case <string>DataSourceType.Veracross:
                newDataSourceConfig = Object.assign(
                    {},
                    dataSourceConfig,
                    {dataSourceType: dataSourceConfig.dataSourceType},
                    {
                        username: deliveryConfigFormGroup.controls.usernameCtrl.value,
                        pass: deliveryConfigFormGroup.controls.passCtrl.value,
                        schoolAbbreviation: deliveryConfigFormGroup.controls.schoolAbbreviationCtrl.value,
                        version: deliveryConfigFormGroup.controls.versionCtrl.value,
                        sisIdPrefix: deliveryConfigFormGroup.controls.sisIdPrefixCtrl.value,
                        isAllowedRemoveRosterData: deliveryConfigFormGroup.controls.isAllowedRemoveRosterDataCtrl.value,
                        isSaveDataSnapshots: !!deliveryConfigFormGroup.controls.isSaveDataSnapshotsCtrl.value,
                        isDetectDuplicatesEnabled: !!deliveryConfigFormGroup.controls.isDetectDuplicatesEnabledCtrl.value,
                        isSupportedUsersWithMultipleRoles: !!deliveryConfigFormGroup.controls.isSupportedUsersWithMultipleRolesCtrl.value,
                        significantChangesMaxPercent: dataSourceConfig.significantChangesMaxPercent || 90,
                        significantUpdatePercent: deliveryConfigFormGroup.controls.significantUpdatePercentCtrl.value,
                        significantDeletePercent: deliveryConfigFormGroup.controls.significantDeletePercentCtrl.value
                    }
                );
                ApplicationSettingsService.trimStringValuesOfObject(newDataSourceConfig);
                break;

            case <string>DataSourceType.OpenApply:
                newDataSourceConfig = Object.assign(
                    {},
                    dataSourceConfig,
                    {dataSourceType: dataSourceConfig.dataSourceType},
                    {
                        baseUrl: deliveryConfigFormGroup.controls.baseUrlCtrl.value,
                        apiKey: deliveryConfigFormGroup.controls.apiKeyCtrl.value,
                        campusId: deliveryConfigFormGroup.controls.campusIdCtrl.value,
                        sisIdPrefix: deliveryConfigFormGroup.controls.sisIdPrefixCtrl.value,
                        isAllowedRemoveRosterData: deliveryConfigFormGroup.controls.isAllowedRemoveRosterDataCtrl.value,
                        isSaveDataSnapshots: !!deliveryConfigFormGroup.controls.isSaveDataSnapshotsCtrl.value,
                        isDetectDuplicatesEnabled: !!deliveryConfigFormGroup.controls.isDetectDuplicatesEnabledCtrl.value,
                        isSupportedUsersWithMultipleRoles: !!deliveryConfigFormGroup.controls.isSupportedUsersWithMultipleRolesCtrl.value,
                        significantChangesMaxPercent: dataSourceConfig.significantChangesMaxPercent || 90,
                        significantUpdatePercent: deliveryConfigFormGroup.controls.significantUpdatePercentCtrl.value,
                        significantDeletePercent: deliveryConfigFormGroup.controls.significantDeletePercentCtrl.value
                    }
                );
                ApplicationSettingsService.trimStringValuesOfObject(newDataSourceConfig);
                break;

            case <string>DataSourceType.ManageBac:
                newDataSourceConfig = Object.assign(
                    {},
                    dataSourceConfig,
                    {dataSourceType: dataSourceConfig.dataSourceType},
                    {
                        baseUrl: deliveryConfigFormGroup.controls.baseUrlCtrl.value,
                        apiKey: deliveryConfigFormGroup.controls.apiKeyCtrl.value,
                        sisIdPrefix: deliveryConfigFormGroup.controls.sisIdPrefixCtrl.value,
                        isAllowedRemoveRosterData: deliveryConfigFormGroup.controls.isAllowedRemoveRosterDataCtrl.value,
                        isSaveDataSnapshots: !!deliveryConfigFormGroup.controls.isSaveDataSnapshotsCtrl.value,
                        isDetectDuplicatesEnabled: !!deliveryConfigFormGroup.controls.isDetectDuplicatesEnabledCtrl.value,
                        isSupportedUsersWithMultipleRoles: !!deliveryConfigFormGroup.controls.isSupportedUsersWithMultipleRolesCtrl.value,
                        significantChangesMaxPercent: dataSourceConfig.significantChangesMaxPercent || 90,
                        significantUpdatePercent: deliveryConfigFormGroup.controls.significantUpdatePercentCtrl.value,
                        significantDeletePercent: deliveryConfigFormGroup.controls.significantDeletePercentCtrl.value
                    }
                );
                ApplicationSettingsService.trimStringValuesOfObject(newDataSourceConfig);
                break;

            case <string>DataSourceType.Qwiklabs:
                newDataSourceConfig = Object.assign(
                    {},
                    dataSourceConfig,
                    {dataSourceType: dataSourceConfig.dataSourceType},
                    {
                        baseUrl: deliveryConfigFormGroup.controls.baseUrlCtrl.value,
                        granularGradeSync: deliveryConfigFormGroup.controls.granularGradeSyncCtrl.value,
                        useDeepLink: deliveryConfigFormGroup.controls.useDeepLinkCtrl.value,
                        sisIdPrefix: deliveryConfigFormGroup.controls.sisIdPrefixCtrl.value,
                        isAllowedRemoveRosterData: deliveryConfigFormGroup.controls.isAllowedRemoveRosterDataCtrl.value,
                        isSaveDataSnapshots: !!deliveryConfigFormGroup.controls.isSaveDataSnapshotsCtrl.value,
                        isDetectDuplicatesEnabled: !!deliveryConfigFormGroup.controls.isDetectDuplicatesEnabledCtrl.value,
                        isSupportedUsersWithMultipleRoles: !!deliveryConfigFormGroup.controls.isSupportedUsersWithMultipleRolesCtrl.value,
                        significantChangesMaxPercent: dataSourceConfig.significantChangesMaxPercent || 90,
                        significantUpdatePercent: deliveryConfigFormGroup.controls.significantUpdatePercentCtrl.value,
                        significantDeletePercent: deliveryConfigFormGroup.controls.significantDeletePercentCtrl.value
                    }
                );
                ApplicationSettingsService.trimStringValuesOfObject(newDataSourceConfig);
                break;

            case <string>DataSourceType.GoogleClassroom:
                newDataSourceConfig = Object.assign(
                    {},
                    dataSourceConfig,
                    {dataSourceType: dataSourceConfig.dataSourceType},
                    {
                        roleAssignmentByEmail: !!deliveryConfigFormGroup.controls.roleAssignmentByEmailCtrl.value,
                        sisIdPrefix: deliveryConfigFormGroup.controls.sisIdPrefixCtrl.value,
                        isAllowedRemoveRosterData: deliveryConfigFormGroup.controls.isAllowedRemoveRosterDataCtrl.value,
                        isSaveDataSnapshots: !!deliveryConfigFormGroup.controls.isSaveDataSnapshotsCtrl.value,
                        isDetectDuplicatesEnabled: !!deliveryConfigFormGroup.controls.isDetectDuplicatesEnabledCtrl.value,
                        isSupportedUsersWithMultipleRoles: !!deliveryConfigFormGroup.controls.isSupportedUsersWithMultipleRolesCtrl.value,
                        significantChangesMaxPercent: dataSourceConfig.significantChangesMaxPercent || 90,
                        significantUpdatePercent: deliveryConfigFormGroup.controls.significantUpdatePercentCtrl.value,
                        significantDeletePercent: deliveryConfigFormGroup.controls.significantDeletePercentCtrl.value
                    }
                );
                ApplicationSettingsService.trimStringValuesOfObject(newDataSourceConfig);
                break;

            case <string>DataSourceType.Aeries:
                newDataSourceConfig = Object.assign(
                    {},
                    dataSourceConfig,
                    {dataSourceType: dataSourceConfig.dataSourceType},
                    {
                        baseUrl: deliveryConfigFormGroup.controls.baseUrlCtrl.value,
                        certificate: deliveryConfigFormGroup.controls.certificateCtrl.value,
                        sisIdPrefix: deliveryConfigFormGroup.controls.sisIdPrefixCtrl.value,
                        isAllowedRemoveRosterData: deliveryConfigFormGroup.controls.isAllowedRemoveRosterDataCtrl.value,
                        isSaveDataSnapshots: !!deliveryConfigFormGroup.controls.isSaveDataSnapshotsCtrl.value,
                        isDetectDuplicatesEnabled: !!deliveryConfigFormGroup.controls.isDetectDuplicatesEnabledCtrl.value,
                        isSupportedUsersWithMultipleRoles: !!deliveryConfigFormGroup.controls.isSupportedUsersWithMultipleRolesCtrl.value,
                        significantChangesMaxPercent: dataSourceConfig.significantChangesMaxPercent || 90,
                        significantUpdatePercent: deliveryConfigFormGroup.controls.significantUpdatePercentCtrl.value,
                        significantDeletePercent: deliveryConfigFormGroup.controls.significantDeletePercentCtrl.value
                    }
                );
                ApplicationSettingsService.trimStringValuesOfObject(newDataSourceConfig);
                break;

            case <string>DataSourceType.Blackboard:
                newDataSourceConfig = Object.assign(
                    {},
                    dataSourceConfig,
                    {dataSourceType: dataSourceConfig.dataSourceType},
                    {
                        host: deliveryConfigFormGroup.controls.hostCtrl.value,
                        blackboardDataSourceKey: deliveryConfigFormGroup.controls.blackboardDataSourceKeyCtrl.value,
                        sisIdPrefix: deliveryConfigFormGroup.controls.sisIdPrefixCtrl.value,
                        isAllowedRemoveRosterData: deliveryConfigFormGroup.controls.isAllowedRemoveRosterDataCtrl.value,
                        isSaveDataSnapshots: !!deliveryConfigFormGroup.controls.isSaveDataSnapshotsCtrl.value,
                        isDetectDuplicatesEnabled: !!deliveryConfigFormGroup.controls.isDetectDuplicatesEnabledCtrl.value,
                        isSupportedUsersWithMultipleRoles: !!deliveryConfigFormGroup.controls.isSupportedUsersWithMultipleRolesCtrl.value,
                        significantChangesMaxPercent: dataSourceConfig.significantChangesMaxPercent || 90,
                        significantUpdatePercent: deliveryConfigFormGroup.controls.significantUpdatePercentCtrl.value,
                        significantDeletePercent: deliveryConfigFormGroup.controls.significantDeletePercentCtrl.value
                    }
                );
                ApplicationSettingsService.trimStringValuesOfObject(newDataSourceConfig);
                break;

            case <string>DataSourceType.GSuite:
                newDataSourceConfig = Object.assign(
                    {},
                    dataSourceConfig,
                    {dataSourceType: dataSourceConfig.dataSourceType},
                    {
                        importGroupsAsSections: !!deliveryConfigFormGroup.controls.importGroupsAsSectionsCtrl.value,
                        exportSectionsAsGroups: !!deliveryConfigFormGroup.controls.exportSectionsAsGroupsCtrl.value,
                        sisIdPrefix: deliveryConfigFormGroup.controls.sisIdPrefixCtrl.value,
                        isAllowedRemoveRosterData: deliveryConfigFormGroup.controls.isAllowedRemoveRosterDataCtrl.value,
                        isSaveDataSnapshots: !!deliveryConfigFormGroup.controls.isSaveDataSnapshotsCtrl.value,
                        isDetectDuplicatesEnabled: !!deliveryConfigFormGroup.controls.isDetectDuplicatesEnabledCtrl.value,
                        isSupportedUsersWithMultipleRoles: !!deliveryConfigFormGroup.controls.isSupportedUsersWithMultipleRolesCtrl.value,
                        significantChangesMaxPercent: dataSourceConfig.significantChangesMaxPercent || 90,
                        significantUpdatePercent: deliveryConfigFormGroup.controls.significantUpdatePercentCtrl.value,
                        significantDeletePercent: deliveryConfigFormGroup.controls.significantDeletePercentCtrl.value
                    }
                );
                ApplicationSettingsService.trimStringValuesOfObject(newDataSourceConfig);
                break;

            default:
                break;
        }

        return newDataSourceConfig;
    }

    public getSFTPConfigFormData(dataSourceConfig: SFTPConfig, deliveryConfigFormGroup: UntypedFormGroup): SFTPConfig {
        let newDataSourceConfig: SFTPConfig;
        const host = deliveryConfigFormGroup.getRawValue().hostCtrl || '';
        const username = deliveryConfigFormGroup.controls.serverTypeCtrl.value === 'gg4l'
            ? deliveryConfigFormGroup.controls.FTPUsernameCtrl.value
            : deliveryConfigFormGroup.controls.usernameCtrl.value;
        const remoteDirectory = deliveryConfigFormGroup.getRawValue().remoteDirectoryCtrl || '';

        newDataSourceConfig = Object.assign(
            {},
            dataSourceConfig,
            {dataSourceType: DataSourceType.SFTP},
            {
                serverType: deliveryConfigFormGroup.controls.serverTypeCtrl.value,
                host: host,
                port: deliveryConfigFormGroup.getRawValue().portCtrl,
                username: username,
                password: deliveryConfigFormGroup.getRawValue().passwordCtrl,
                remoteDirectory: remoteDirectory,
                isCompressFiles: deliveryConfigFormGroup.controls.isCompressFilesCtrl.value,
                archiveName: deliveryConfigFormGroup.controls.archiveNameCtrl.value,
                isEmailNotification: deliveryConfigFormGroup.controls.isEmailNotificationCtrl.value,
                emailAddress: deliveryConfigFormGroup.controls.emailAddressCtrl.value,
                isEncryptDataInFiles: deliveryConfigFormGroup.controls.isEncryptDataInFilesCtrl.value,
                defaultSchemaName: deliveryConfigFormGroup.controls.dataSchemaCtrl.value,
                isCreateProviderFolder: !!deliveryConfigFormGroup.controls.createProviderFolderCtrl.value,
                encryptSecretKeyTimestamp: deliveryConfigFormGroup.controls.encryptSecretKeyTimestampCtrl.value,
                sisIdPrefix: deliveryConfigFormGroup.controls.sisIdPrefixCtrl.value,
                isAllowedRemoveRosterData: deliveryConfigFormGroup.controls.isAllowedRemoveRosterDataCtrl.value,
                isCreateFolderForEachSchool: deliveryConfigFormGroup.controls.isCreateFolderForEachSchoolCtrl.value,
                isSaveDataSnapshots: !!deliveryConfigFormGroup.controls.isSaveDataSnapshotsCtrl.value,
                isDetectDuplicatesEnabled: !!deliveryConfigFormGroup.controls.isDetectDuplicatesEnabledCtrl.value,
                isSupportedUsersWithMultipleRoles: !!deliveryConfigFormGroup.controls.isSupportedUsersWithMultipleRolesCtrl.value,
                contactNamePattern: deliveryConfigFormGroup.controls.contactNamePatternCtrl.value?.trim() || '',
                classlessInterDomainRouting: deliveryConfigFormGroup.controls.classlessInterDomainRoutingCtrl.value?.trim() || '',
                significantChangesMaxPercent: dataSourceConfig.significantChangesMaxPercent || 90,
                significantUpdatePercent: deliveryConfigFormGroup.controls.significantUpdatePercentCtrl.value,
                significantDeletePercent: deliveryConfigFormGroup.controls.significantDeletePercentCtrl.value
            }
        );

        if (deliveryConfigFormGroup.controls.dataSchemaCtrl.value === DefaultSchemaName.RANK_ONE &&
            (<SFTPConfig>dataSourceConfig).rankOneDistrictList) {
            const district = (<RankOneDistrictItem[]>(<SFTPConfig>dataSourceConfig).rankOneDistrictList)
                .find((rankOneDistrict: RankOneDistrictItem) =>
                    rankOneDistrict.name === deliveryConfigFormGroup.controls.rankOneDistrictCtrl.value);

            if (district !== undefined) {
                newDataSourceConfig = Object.assign(
                    newDataSourceConfig,
                    {
                        rankOneDistrictName: deliveryConfigFormGroup.controls.rankOneDistrictCtrl.value,
                        rankOneDistrictId: district.id
                    }
                );
            }
        }

        ApplicationSettingsService.trimStringValuesOfObject(newDataSourceConfig);
        return newDataSourceConfig;
    }

    public modifyDataSourceConfigBeforeSave(dataSourceConfig: DataSourceConfig,
        application: Application = null,
        defaultApplications: Application[] = []): DataSourceConfig {

        const defaultApp = this.getDefaultAppByIcon(application, defaultApplications);
        const defaultAppName = defaultApp ? defaultApp.name : '';
        let baseUrl;

        if ((dataSourceConfig.dataSourceType === DataSourceType.SFTP)
            && application.modes
            && (application.modes.length > 0)
            && (application.modes[0] === IntegrationMode.IMPORT)
        ) {
            if ((<SFTPConfig>dataSourceConfig).host === environment.customer['sftpHost']) {
                (<SFTPConfig>dataSourceConfig).host = 'localhost';

                if ((<SFTPConfig>dataSourceConfig).port === environment.customer['sftpPort']) {
                    (<SFTPConfig>dataSourceConfig).port = DEFAULT_SFTP_LOCALHOST_PORT;
                }
            }
        }

        let isOneRoster12 = false;
        if (defaultApp && (dataSourceConfig.dataSourceType === DataSourceType.OneRoster)) {
            if ((<OneRosterConfig>dataSourceConfig).oneRosterBaseUrl && ((<OneRosterConfig>dataSourceConfig).oneRosterBaseUrl !== '')) {
                (<OneRosterConfig>dataSourceConfig).oneRosterBaseUrl = (<OneRosterConfig>dataSourceConfig).oneRosterBaseUrl.trim();
                const length = (<OneRosterConfig>dataSourceConfig).oneRosterBaseUrl.length;
                if ((length > 1) && ((<OneRosterConfig>dataSourceConfig).oneRosterBaseUrl[length - 1] === '/')) {
                    (<OneRosterConfig>dataSourceConfig).oneRosterBaseUrl =
                        (<OneRosterConfig>dataSourceConfig).oneRosterBaseUrl.substring(0, length - 1);
                }

                const lastMatch2 = (<OneRosterConfig>dataSourceConfig).oneRosterBaseUrl.lastIndexOf(ONEROSTER_V12_SUFFIX);

                if (lastMatch2 > -1) {
                    isOneRoster12 = true;
                } else {
                    const lastMatch1 = (<OneRosterConfig>dataSourceConfig).oneRosterBaseUrl.lastIndexOf(ONEROSTER_V11_SUFFIX);

                    if (lastMatch1 > -1) {
                        baseUrl = (<OneRosterConfig>dataSourceConfig).oneRosterBaseUrl.substring(0, lastMatch1);
                    } else {
                        baseUrl = (<OneRosterConfig>dataSourceConfig).oneRosterBaseUrl;

                        const baseUrlSuffix = getDataSourceModifyParam(dataSourceConfig.dataSourceType, defaultAppName, 'baseUrlSuffix');
                        if (baseUrlSuffix) {
                            baseUrl += baseUrlSuffix;
                        }

                        (<OneRosterConfig>dataSourceConfig).oneRosterBaseUrl = baseUrl;

                        const baseUrlOneRosterSuffix = getDataSourceModifyParam(dataSourceConfig.dataSourceType, defaultAppName, 'baseUrlOneRosterSuffix');
                        if (baseUrlOneRosterSuffix || (baseUrlOneRosterSuffix === null)) {
                            (<OneRosterConfig>dataSourceConfig).oneRosterBaseUrl += ONEROSTER_V11_SUFFIX;
                        }
                    }
                }
            }
        }

        const authUrlSuffix = getDataSourceModifyParam(dataSourceConfig.dataSourceType, defaultAppName, 'authUrlSuffix');
        const shouldWeSetDefaultAuthUrl = (!OneRosterDataSourceFriendlyNamesWithEnteredByUserAuthUrl.includes(dataSourceConfig.friendlyName)
                || !(<OneRosterConfig>dataSourceConfig).authUrl);
        if (defaultApp
            && !isOneRoster12
            && authUrlSuffix
            && shouldWeSetDefaultAuthUrl
        ) {
            if (baseUrl) {
                (<OneRosterConfig>dataSourceConfig).authUrl = baseUrl + authUrlSuffix;
            } else {
                (<OneRosterConfig>dataSourceConfig).authUrl = '';
            }
        }

        if ((<DataSourceType>application.dataSourceType === DataSourceType.LTI) && !(<LTIConfig>dataSourceConfig).applicationGuid) {
            (<LTIConfig>dataSourceConfig).applicationGuid = application.guid;
        }

        // if (dataSourceConfig.dataSourceType === DataSourceType.Coursera) {
        //     const parser = document.createElement('a');
        //     (<CourseraConfig>dataSourceConfig).redirectUri = `${parser.baseURI}admin/applications/coursera/callback`;
        // }

        if (application?.ownerId) {
            dataSourceConfig.orgGuid = this.auditUserActivityService.orgGuid || application.ownerId;
        }

        return dataSourceConfig;
    }

    public modifyDataSourceConfigAfterLoad(dataSourceConfig: DataSourceConfig,
        application: Application = null,
        defaultApplications: Application[] = []): DataSourceConfig {

        const defaultApp = this.getDefaultAppByIcon(application, defaultApplications);
        const defaultAppName = defaultApp ? defaultApp.name : '';

        if ((dataSourceConfig.dataSourceType === DataSourceType.SFTP)
            && application.modes
            && (application.modes.length > 0)
            && (application.modes[0] === IntegrationMode.IMPORT)
        ) {
            if ((<SFTPConfig>dataSourceConfig).host === 'localhost') {
                (<SFTPConfig>dataSourceConfig).host = environment.customer['sftpHost'];
                (<SFTPConfig>dataSourceConfig).port = (environment.customer['sftpPort'] || DEFAULT_SFTP_LOCALHOST_PORT);
            }
        }

        if (dataSourceConfig.dataSourceType === DataSourceType.OneRoster) {
            if (defaultApp) {
                const baseUrlOneRosterSuffix = getDataSourceModifyParam(dataSourceConfig.dataSourceType, defaultAppName, 'baseUrlOneRosterSuffix');
                if (baseUrlOneRosterSuffix || (baseUrlOneRosterSuffix === null)) {
                    const lastMatch = (<OneRosterConfig>dataSourceConfig).oneRosterBaseUrl.lastIndexOf(ONEROSTER_V11_SUFFIX);

                    if (lastMatch > -1) {
                        (<OneRosterConfig>dataSourceConfig).oneRosterBaseUrl =
                            (<OneRosterConfig>dataSourceConfig).oneRosterBaseUrl.substring(0, lastMatch);
                    }
                }

                const baseUrlSuffix = <string>getDataSourceModifyParam(dataSourceConfig.dataSourceType, defaultAppName, 'baseUrlSuffix');
                if (baseUrlSuffix) {
                    const lastMatchBaseUrlSuffix = (<OneRosterConfig>dataSourceConfig).oneRosterBaseUrl.lastIndexOf(baseUrlSuffix);

                    if (lastMatchBaseUrlSuffix > -1) {
                        (<OneRosterConfig>dataSourceConfig).oneRosterBaseUrl =
                            (<OneRosterConfig>dataSourceConfig).oneRosterBaseUrl.substring(0, lastMatchBaseUrlSuffix);
                    }
                }
            }
        }

        return dataSourceConfig;
    }

    public isOrgsMappingEnabled(integration: Integration, application: Application, dataSourceConfig: DataSourceConfig) {
        let isOrgsMappingEnabled = false;
        const isAppCreated = application.configurationStep === APP_LAST_STEP;

        if (integration
            && integration.hasMultipleOrgs
            && ((!integration.organizationsMappingModel // Temporary fix to show Orgs Mapping step at once for Blackboard Sync DS
                    && (integration.source.dataSourceType === DataSourceType.Blackboard))
                || (integration.organizationsMappingModel
                    && !((integration.source.dataSourceType === DataSourceType.EduTone)
                        && integration.organizationsMappingModel
                        && integration.organizationsMappingModel.targetOrganizations
                        && (integration.organizationsMappingModel.targetOrganizations.length === 0)))
                || (integration.source.dataSourceType === DataSourceType.EduTone
                    && ((dataSourceConfig.dataSourceType === DataSourceType.LTI)
                        || (dataSourceConfig.dataSourceType === DataSourceType.GoogleClassroom))))
            && (integration.target.dataSourceType !== DATA_SOURCE_TYPE.SFTP)
            && !ApplicationSettingsService.isSupportMappingRulesDataSourceTypes(application, dataSourceConfig, integration)
        ) {
            if (!isAppCreated && dataSourceConfig && dataSourceConfig.isManuallyUpload) {
                isOrgsMappingEnabled = false;
            } else {
                isOrgsMappingEnabled = true;
            }
        }
        return isOrgsMappingEnabled;
    }
}

export class SortedOptions {
    constructor(
        public equalValuesCount: number = 0,
        public minimalIncludedIndex: number = 999999
    ) { }
}

export const FAKE_PASSWORD_VALUE = '●●●●●●●●●●●●●●●●●●●●●●●●●●';
export const STAFFS = 'staffs';
export const SCHOOL_ADMINS = 'school_admins';
export const AMPLIFY_STAFF_ACCESS_REQUESTED_ENTITIES = [STAFFS, SCHOOL_ADMINS];
export const LOCAL_ID = 'localId';
export const FAMILY_NAME = 'name.familyName';
export const GIVEN_NAME = 'name.givenName';
export const EMAIL_ADDRESS = 'email.emailAddress';
export const AMPLIFY_STAFF_ACCESS_REQUESTED_PROPERTY_NAMES = [LOCAL_ID, FAMILY_NAME, GIVEN_NAME, EMAIL_ADDRESS];

export const SECTIONS = 'rosters';
export const COURSES = 'courses';
export const COURSE_TITLE = 'courseTitle';
export const SECTION_NAME = 'sectionName';
export const SECTIONS_ALIAS = 'sectionAlias';
export const SECTION_NUMBER = 'sectionNumber';
export const GUID = 'guid';
export const QWIKLABS_CLASSES_REQUESTED_PROPERTY_NAMES_FOR_COURSES = [LOCAL_ID, COURSE_TITLE];
export const QWIKLABS_CLASSES_REQUESTED_PROPERTY_NAMES_FOR_SECTIONS = [LOCAL_ID, SECTION_NAME, SECTIONS_ALIAS, SECTION_NUMBER, GUID];

export const DATA_SOURCE_CONFIG_COURSERA_ID = 'data-source-config-coursera-id';
export const DATA_SOURCE_CONFIG_GOOGLE_OBJECT_ID = 'data-source-config-google-classroom-object-id';
export const ROSTER_STUDENT_ENROLLMENT = 'RosterStudentEnrollment';

export const RESTRICTED_ONE_ROSTER_1_1_CSV_IMPORT_ATTRIBUTES = {
    'ellStatus': 'ellStatus',
    // 'lunchStatus': 'lunchStatus',
    'specEducationStatus': 'specEducationStatus',
    'disabilityOrExceptionality': 'disabilityOrExceptionality',
    'secondDisability': 'secondDisability',
    'thirdDisability': 'thirdDisability',
    'placement': 'placement',
    'spedExitCode': 'spedExitCode',
    'transportationFlag': 'transportationFlag',
    'demographics.languages': 'demographics.languages'
};

export const ATTRIBUTES_THAT_COULD_BE_MAPPED_AS_TARGET_MORE_THAN_ONCE_OBJ = {
    guid: 'guid',
    stateId: 'stateId',
    studentNumber: 'studentNumber',
    schoolNumber: 'schoolNumber',
    districtId: 'districtId',
    schoolId: 'schoolId',
    schoolName: 'schoolName',
    ncesId: 'ncesId',
    localId: 'Local Id',
    staffNumber: 'Staff Number',
    sectionId: 'Section Id',
    sectionNumber: 'Section Number'
};

export const RESTRICTED_AMPLIFY_SKIN_TARGET_ATTRIBUTES = {
    'sisIds[canv_id]': 'sisIds[canv_id]',
    'sisIds[coursera_id]': 'sisIds[coursera_id]',
    'sisIds[ed_fi_id]': 'sisIds[ed_fi_id]',
    'sisIds[google_classroom_id]': 'sisIds[google_classroom_id]',
    'sisIds[google_id]': 'sisIds[google_id]',
    'sisIds[ill_id]': 'sisIds[ill_id]',
    'sisIds[ldap_id]': 'sisIds[ldap_id]',
    'sisIds[ms_sds_id]': 'sisIds[ms_sds_id]',
    'sisIds[orgUnit]': 'sisIds[orgUnit]',
    'sisIds[inow_id]': 'sisIds[inow_id]',
    'sisIds[mb_id]': 'sisIds[mb_id]',
    'sisIds[oa_id]': 'sisIds[oa_id]',
    'sisIds[ps_id]': 'sisIds[ps_id]',
    'sisIds[veracross_id]': 'sisIds[veracross_id]',
    'sisIds[xpr_id]': 'sisIds[xpr_id]'
};

export const ONE_ROSTER_SOURCED_ID_DELIMITER = '###';

export const LOGIN_CREDENTIALS_ATTRIBUTES_MAP = new Map([
    ['birthday', 'demographics.birthDate'],
    ['firstName', 'name.givenName'],
    ['lastName', 'name.familyName'],
    ['middleName', 'name.middleName'],
    ['primarySchoolName', 'primarySchoolName'],
    ['sisId', 'localId'],
    ['stateId', 'stateId'],
    ['studentNumber', 'studentNumber'],
    ['teacherNumber', 'staffNumber'],
    ['username', 'credentials.username']
]);
export const REQUEST_LOGIN_CREDENTIALS_ATTRIBUTES_MAP = new Map([
    ['demographics.birthDate', 'birthday'],
    ['name.givenName', 'firstName'],
    ['name.familyName', 'lastName'],
    ['name.middleName', 'middleName'],
    ['primarySchoolName', 'primarySchoolName'],
    ['localId', 'sisId'],
    ['stateId', 'stateId'],
    ['studentNumber', 'studentNumber'],
    ['staffNumber', 'teacherNumber'],
    ['credentials.username', 'username']
]);
